import {
  TextField,
  Button,
  FormControl,
  IconButton,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  Box,
  Typography,
  Divider,
  InputLabel,
  Drawer,
  Collapse,
  Card,
  Chip,
  Checkbox,
  ListItemText,
} from "@mui/material";
import React, {
  useState,
  useEffect,
  forwardRef,
  useRef,
  useImperativeHandle,
} from "react";
import {
  Download,
  Add,
  CalendarToday,
  Close as CloseIcon,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import GlobalFilter from "../../components/GlobalFilter/GlobalFilter";
import UserSelection from "./UserSelection";
import ScheduleForm from "../onPremises/newScan/ScheduleForm";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import { toast } from "react-toastify";
import { SkeletonLoader } from "../../modules/dashboard/shared";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ResourceReportCustomise from "./ResourceReportCustomise";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import eventEmitter from "../../components/utils/eventEmitter";
import useFrameworks from "../../shared/hooks/useFrameworks";

dayjs.extend(utc);

export interface CreateNewReportHandle {
  buildPayload: () => any;
  buildUpdatePayload: (
    is_scheduled?: boolean,
    is_download?: boolean,
    scheduleData?: any
  ) => any;
}

interface CreateNewReportProps {
  selectedReportType: any | null;
  onCancel: () => void;
  hideButtons?: boolean;
  reportData: any;
  isEditable?: boolean;
  isEditMode?: boolean;
  onEditModeChange?: (isEdit: boolean) => void;
  onRefreshReports?: () => void;
  triggerScheduleDrawer?: boolean;
  onScheduleDrawerTriggered?: () => void;
  onScheduleUpdate?: (scheduleData: any) => void;
}

interface ComplianceOption {
  value: string;
  label: string;
  controlType?: string;
  version?: string;
  versionId?: string;
  controlTypeId?: string;
}

const CreateNewReport = forwardRef<CreateNewReportHandle, CreateNewReportProps>(
  (
    {
      onCancel,
      selectedReportType,
      hideButtons = false,
      reportData,
      isEditable = true,
      onRefreshReports,
      triggerScheduleDrawer = false,
      onScheduleDrawerTriggered,
      onScheduleUpdate,
      isEditMode,
    },
    ref
  ): React.ReactElement => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const { isFrameworkOn } = useFrameworks();
    // Dynamic compliance options - will be populated from API
    const [complianceOptions, setComplianceOptions] = useState<
      ComplianceOption[]
    >([]);
    // Dynamic resource compliance options - will be populated from API
    const [resourceComplianceOptions, setResourceComplianceOptions] = useState<
      ComplianceOption[]
    >([]);
    const [processingButton, setProcessingButton] = useState<
      "schedule" | "download" | null
    >(null);
    const [isLoading, setIsLoading] = useState(false);
    const [assetsLoader, setAssetsLoader] = useState(false);
    const [resourcesLoader, setResourcesLoader] = useState(false);
    const [usersLoader, setUsersLoader] = useState(false);
    const leftGridRef = useRef<HTMLDivElement>(null);
    const [formData, setFormData] = useState({
      reportName: "",
      description: "",
      reportFormat: "Excel",
      logOutput: "Exclude",
      remediation: "Exclude",
      riskAccepted: "Exclude",
      recommendation: "1",
      assets: "",
      resource: "",
      user: "",
      benchmarks: "2",
      assetCompliance: [] as string[], // Will be set after API call
      resourceCompliance: [] as string[], // Will be set after API call
      cveDescription: "Include",
      complianceDescription: "Exclude",
      mitreDescription: "Exclude",
      assetMitre: "Exclude",
      resourceMitre: "Exclude",
      resourceMitreDescription: "Exclude",
      impact: "Exclude",
      descriptionInclude: "Include",
      selectedAssetsCount: 0,
      selectedResourcesCount: 0,
      orgPostureScope: "",
      orgOnPremRiskAccepted: "Exclude",
    });
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [selectedFrequency, setSelectedFrequency] = useState<any | null>(
      null
    );
    const [selectedDate, setSelectedDate] = useState<any | null>(null);
    const [selectedDay, setSelectedDay] = useState<any | null>(null);
    const [selectedHour, setSelectedHour] = useState<string>("");
    const [selectedMinute, setSelectedMinute] = useState<string>("");
    const [selectedAmPm, setSelectedAmPm] = useState<string>("");
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
    const [selectedAssetsCount, setSelectedAssetsCount] = useState<number>(0);
    const [selectedResourcesCount, setSelectedResourcesCount] =
      useState<number>(0);
    const [selectedAssets, setSelectedAssets] = useState<any[]>([]);
    const [selectedResources, setSelectedResources] = useState<any[]>([]);
    const [completeFilterData, setCompleteFilterData] = useState<any>({});
    const [hourlyInterval, setHourlyInterval] = useState<string>("");
    const [hourlyMinute, setHourlyMinute] = useState<string>("");
    const [selectedTime, setSelectedTime] = useState<any | null>(null);
    const [scheduleOptions, setScheduleOptions] = useState<any>({
      frequency: [],
      Days: [],
    });
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [isOutputSettingsExpanded, setIsOutputSettingsExpanded] =
      useState(true);
    const [isAssetPostureExpanded, setIsAssetPostureExpanded] = useState(true);
    const [isFiltersSettingsExpanded, setIsFiltersSettingsExpanded] =
      useState(true);
    const [validationErrors, setValidationErrors] = useState({
      reportName: "",
    });
    const [assetData, setAssetData] = useState<any>([]);
    const [cloudResourceData, setCloudResourceData] = useState<any>([]);
    const [preSelectedAssetIds, setPreSelectedAssetIds] = useState<string[]>(
      []
    );
    const [preSelectedResourceIds, setPreSelectedResourceIds] = useState<
      string[]
    >([]);
    // Saved selections: keep the last custom selection so we can reapply when user toggles back to Custom
    const [savedPreSelectedAssetIds, setSavedPreSelectedAssetIds] = useState<
      string[]
    >([]);
    const [savedPreSelectedResourceIds, setSavedPreSelectedResourceIds] =
      useState<string[]>([]);

    const isOnlyCloudUser = useSelector(
      (state: RootState) => state.UserLoginDetails.isOnlyCloudUser
    );
    const isOnlyPremAssetUser = useSelector(
      (state: RootState) => state.UserLoginDetails.isOnlyPremAssetUser
    );
    const isBothCloudAndPremAssetUser = useSelector(
      (state: RootState) => state.UserLoginDetails.isBothCloudAndPremAssetUser
    );

    const [isCloudSelected, setIsCloudSelected] = useState<boolean>(true);
    const [isOnPremSelected, setIsOnPremSelected] = useState<boolean>(true);

    const processFilterDataForPosture = (filterData: any, fullTreeData?: any) => {
      let isCloud = false;
      let isOnPrem = false;

      if (fullTreeData && Array.isArray(fullTreeData)) {
          // When we have the full tree from GlobalFilter (user just clicked Apply),
          // use node-level `selected` state for accuracy.
          const isNodeOrChildSelected = (node: any): boolean => {
              if (!node) return false;
              if (node.selected) return true;
              if (node.data && Array.isArray(node.data)) {
                  return node.data.some(isNodeOrChildSelected);
              }
              return false;
          };

          const findNodeByType = (nodes: any[], type: string): any => {
              if (!Array.isArray(nodes)) return undefined;
              for (const node of nodes) {
                  if (node.type === type) return node;
                  if (node.data) {
                      const found = findNodeByType(node.data, type);
                      if (found) return found;
                  }
              }
              return undefined;
          };

          const cloudNode = findNodeByType(fullTreeData, "cloud");
          const onpremNode = findNodeByType(fullTreeData, "onprem");

          isCloud = isNodeOrChildSelected(cloudNode);
          isOnPrem = isNodeOrChildSelected(onpremNode);
      } else {
          // When called from initial page load (GET GLOBAL_DASHBOARD_REGION response),
          // the data has list properties with objects that have .selected fields.
          const checkAnySelected = (list: any[]): boolean => {
              if (!list || !Array.isArray(list)) return false;
              for (const item of list) {
                  if (item && typeof item === 'object' && item.selected) return true;
                  if (item && typeof item === 'object' && item.data && checkAnySelected(item.data)) return true;
              }
              return false;
          };
          const hasOriginSelected = (list: any[], origin: string): boolean => {
              if (!list || !Array.isArray(list)) return false;
              for (const item of list) {
                  if (!item || typeof item !== 'object') continue;
                  if (item.origin === origin && item.selected) return true;
                  if (item.data && hasOriginSelected(item.data, origin)) return true;
              }
              return false;
          };

          isCloud = 
              !!(filterData.all_cloud || 
              filterData.all_cloud_accounts || 
              filterData.all_cloud_resource_types || 
              filterData.all_cloud_regions ||
              checkAnySelected(filterData.cloud_accounts_list) ||
              checkAnySelected(filterData.cloud_resource_types_list) ||
              checkAnySelected(filterData.cloud_regions_list) ||
              hasOriginSelected(filterData.vendor_list, 'cloud'));

          isOnPrem = 
              !!(filterData.all_regions ||
              filterData.all_categories ||
              filterData.all_models ||
              checkAnySelected(filterData.regions_list) ||
              checkAnySelected(filterData.category_list) ||
              checkAnySelected(filterData.model_list) ||
              hasOriginSelected(filterData.vendor_list, 'onprem'));
      }

      if (Object.keys(filterData).length > 0 || (fullTreeData && fullTreeData.length > 0)) {
          setIsCloudSelected(isCloud);
          setIsOnPremSelected(isOnPrem);
          
          // Always set the scope to the correct value based on what is available.
          // This handles both upgrades (on_prem -> all) and downgrades (all -> cloud).
          setFormData(prev => {
            let newScope: string;
            if (isCloud && isOnPrem) {
              // Both cloud and on-prem available — always default to 'all'
              newScope = 'all';
            } else if (isCloud && !isOnPrem) {
              newScope = 'cloud';
            } else if (!isCloud && isOnPrem) {
              newScope = 'on_prem';
            } else {
              newScope = prev.orgPostureScope; // fallback if neither detected
            }
            return { ...prev, orgPostureScope: newScope };
          });
      }
    };

    useEffect(() => {
      if (selectedReportType?.name === "Organization Posture report") {
        const fetchFilterData = async () => {
          try {
            const filterResponse = await axiosInstance.get(END_POINTS.GLOBAL_DASHBOARD_REGION);
            const filterData = filterResponse?.data?.data || {};
            processFilterDataForPosture(filterData);
          } catch (e) {
             console.error("Failed to fetch global filter data", e);
          }
        };
        fetchFilterData();
      }
    }, [selectedReportType?.name]);

    const postureOptions = React.useMemo(() => {
      if (isOnlyCloudUser) {
        return [{ label: "Cloud", value: "cloud", disabled: !isCloudSelected }];
      }

      if (isOnlyPremAssetUser) {
        return [{ label: "On-prem", value: "on_prem", disabled: !isOnPremSelected }];
      }

      if (isBothCloudAndPremAssetUser) {
        return [
          { label: "On-prem", value: "on_prem", disabled: !isOnPremSelected },
          { label: "Cloud", value: "cloud", disabled: !isCloudSelected },
          { label: "All", value: "all", disabled: (!isCloudSelected || !isOnPremSelected) },
        ];
      }

      return [];
    }, [
      isOnlyCloudUser,
      isOnlyPremAssetUser,
      isBothCloudAndPremAssetUser,
      isCloudSelected,
      isOnPremSelected
    ]);

    // Validation functions
    const validateReportName = (value: string): string => {
      if (!value.trim()) {
        return "Report name is required";
      }
      // Check for leading or trailing spaces
      if (value !== value.trim()) {
        return "Report name cannot have leading or trailing spaces";
      }
      // Check if it contains spaces (should use underscores instead)
      if (/\s/.test(value)) {
        return "Report name should use underscores instead of spaces (e.g., report_summary)";
      }
      // Check if it contains numbers
      if (/\d/.test(value)) {
        return "Report name should not contain numbers";
      }
      // Check if it follows the pattern (only letters and underscores, no special characters or numbers)
      if (!/^[a-zA-Z_]+$/.test(value)) {
        return "Report name should contain only letters and underscores";
      }
      return "";
    };

    // Function to reset form data
    const resetFormData = () => {
      setFormData({
        reportName: "",
        description: "",
        reportFormat: "Excel",
        logOutput: "Exclude",
        remediation: "Exclude",
        riskAccepted: "Exclude",
        recommendation: "1",
        assets: "",
        resource: "",
        user: "",
        benchmarks: "2",
        assetCompliance: [] as string[], // Will be set after API call
        resourceCompliance: [] as string[], // Will be set after API call
        cveDescription: "Include",
        complianceDescription: "Exclude",
        mitreDescription: "Exclude",
        assetMitre: "Exclude",
        resourceMitre: "Exclude",
        resourceMitreDescription: "Exclude",
        impact: "Exclude",
        descriptionInclude: "Include",
        selectedAssetsCount: 0,
        selectedResourcesCount: 0,
        orgPostureScope: "",
        orgOnPremRiskAccepted: "Exclude",
      });
      setSelectedUsers([]);
      setSelectedAssetsCount(0);
      setSelectedAssets([]);
      setSelectedResourcesCount(0);
      setSelectedResources([]);
      setCompleteFilterData({});
      setSelectedFrequency(null);
      setSelectedDate(null);
      setSelectedDay(null);
      setSelectedHour("");
      setSelectedMinute("");
      setSelectedAmPm("");
      setSelectedTime(null);
      setHourlyInterval("");
      setHourlyMinute("");
      setValidationErrors({
        reportName: "",
      });
    };

    // Validation function to check if main form is complete
    const isMainFormValid = () => {
      // Check for validation errors first
      if (validationErrors.reportName) {
        return false;
      }

      const hasValidCustomAssets =
        formData.assets !== "2" ||
        selectedAssetsCount > 0 ||
        preSelectedAssetIds.length > 0;
      const hasValidCustomResources =
        formData.resource !== "2" ||
        selectedResourcesCount > 0 ||
        preSelectedResourceIds.length > 0;

      // Check basic required fields that apply to all report types
      const basicFieldsValid =
        formData.reportName.trim() !== "" && !!formData.reportFormat;

      // Check report type specific fields
      if (selectedReportType?.name === "Asset Report") {
        // Asset reports require: assets, assetCompliance, benchmarks, all posture fields
        const assetFieldsValid =
          formData.assets !== "" &&
          hasValidCustomAssets &&
          formData.assetCompliance.length > 0 &&
          formData.benchmarks !== "" &&
          formData.logOutput !== "" &&
          formData.remediation !== "" &&
          formData.riskAccepted !== "" &&
          formData.recommendation !== "" &&
          formData.impact !== "" &&
          formData.descriptionInclude !== "";

        return basicFieldsValid && assetFieldsValid;
      }

      if (selectedReportType?.name === "Resource Report") {
        // Resource reports require: resource, resourceCompliance, benchmarks, resourceMitre, resourceMitreDescription, impact, descriptionInclude
        const resourceFieldsValid =
          formData.resource !== "" &&
          hasValidCustomResources &&
          formData.resourceCompliance.length > 0 &&
          formData.benchmarks !== "" &&
          formData.resourceMitre !== "" &&
          formData.resourceMitreDescription !== "" &&
          formData.impact !== "" &&
          formData.descriptionInclude !== "";

        return basicFieldsValid && resourceFieldsValid;
      }

      if (selectedReportType?.name === "Resource Summary Report") {
        // Resource Summary reports require: resource and resourceCompliance
        const resourceSummaryFieldsValid =
          formData.resource !== "" &&
          hasValidCustomResources &&
          formData.resourceCompliance.length > 0;

        return basicFieldsValid && resourceSummaryFieldsValid;
      }

      // For Organization reports (CVE, Compliance, Summary), require assets field
      if (
        selectedReportType?.name === "Organization CVE Report" ||
        selectedReportType?.name === "Organization Compliance Report" ||
        selectedReportType?.name === "Organization Summary Report"
      ) {
        // Organization reports require: assets field must be filled
        const organizationFieldsValid =
          formData.assets !== "" && hasValidCustomAssets;

        return basicFieldsValid && organizationFieldsValid;
      }

      // For Asset Summary Report, require assets field
      if (selectedReportType?.name === "Asset Summary Report") {
        // Asset Summary reports require: assets field must be filled
        const assetSummaryFieldsValid =
          formData.assets !== "" && hasValidCustomAssets;

        return basicFieldsValid && assetSummaryFieldsValid;
      }

      // For other report types, only basic fields are required
      return basicFieldsValid;
    };

    const isWeekly = () => {
      const frequencyObj = scheduleOptions?.frequency?.find(
        (f: any) => f.id === selectedFrequency
      );
      return frequencyObj?.name === "Weekly";
    };

    const isHourly = () => {
      const frequencyObj = scheduleOptions?.frequency?.find(
        (f: any) => f.id === selectedFrequency
      );
      return frequencyObj?.name === "Hourly";
    };

    const isOtherFrequency = () => {
      return selectedFrequency && !isWeekly() && !isHourly();
    };

    // Validation function to check if schedule form is complete
    const isScheduleFormValid = () => {
      if (!selectedFrequency) return false;

      const frequencyObj = scheduleOptions?.frequency?.find(
        (f: any) => f.id === selectedFrequency
      );
      const frequencyName = frequencyObj?.name;

      // For Weekly frequency: need day, hour, minute, and AM/PM
      if (frequencyName === "Weekly") {
        return selectedDay && selectedHour && selectedMinute && selectedAmPm;
      }

      // For Hourly frequency: user inputs hour (interval), minute, and AM/PM
      if (frequencyName === "Hourly") {
        return (hourlyInterval || selectedHour) && (hourlyMinute || selectedMinute) && selectedAmPm;
      }

      // For other frequencies (One Time, Daily, Monthly): need date, hour, minute, and AM/PM
      if (
        frequencyName === "One Time" ||
        frequencyName === "Daily" ||
        frequencyName === "Monthly"
      ) {
        return selectedDate && selectedHour && selectedMinute && selectedAmPm;
      }

      return false;
    };

    const handleFrequencyChange = (e: any) => {
      const value = e.target.value;
      setSelectedFrequency(value);
      setSelectedDate(null);
      setSelectedDay(null);
      setSelectedHour("");
      setSelectedMinute("");
      setSelectedAmPm("");
      setSelectedTime(null);
      setHourlyInterval("");
      setHourlyMinute("");
    };

    const handleInputChange = (field: string, value: string | string[]) => {
      // Validate report name and description
      if (field === "reportName" && typeof value === "string") {
        const error = validateReportName(value);
        setValidationErrors((prev) => ({ ...prev, reportName: error }));
      }

      setFormData((prev) => {
        const newFormData = {
          ...prev,
          [field]: value,
        };
        return newFormData;
      });

      // Reset counts when dropdown selection changes
      if (field === "assets") {
        // If switching away from Custom ("2"), clear current selection but save it for reapplying later
        if (value !== "2") {
          // Save current custom selection so it can be reapplied when user goes back to Custom
          const savedIds =
            completeFilterData && completeFilterData.devices_list
              ? completeFilterData.devices_list.map((a: any) => a.id)
              : Array.isArray(preSelectedAssetIds) &&
                preSelectedAssetIds.length > 0
                ? preSelectedAssetIds
                : selectedAssets
                  .map((a: any) => a.id || a.asset_id || "")
                  .filter((id: string) => id !== "");
          if (Array.isArray(savedIds) && savedIds.length > 0)
            setSavedPreSelectedAssetIds(savedIds as string[]);

          // Clear currently active custom selection
          setPreSelectedAssetIds([]);
          setSelectedAssetsCount(0);
          // Also clear any completeFilterData devices_list so UI reflects no selection
          setCompleteFilterData((prev: any) => {
            const copy = { ...prev };
            if (copy.devices_list) delete copy.devices_list;
            return copy;
          });
        } else {
          // Switched to Custom: reapply any saved selection so it is shown in the drawer
          if (
            Array.isArray(preSelectedAssetIds) &&
            preSelectedAssetIds.length === 0 &&
            Array.isArray(savedPreSelectedAssetIds) &&
            savedPreSelectedAssetIds.length > 0
          ) {
            setPreSelectedAssetIds(savedPreSelectedAssetIds);
            setSelectedAssetsCount(savedPreSelectedAssetIds.length);
          }
        }
      }

      if (field === "user" && value !== "2") {
        setSelectedUsers([]);
      }

      if (field === "resource") {
        if (value !== "2") {
          const savedIds =
            completeFilterData && completeFilterData.resources_list
              ? completeFilterData.resources_list.map((r: any) => r.id)
              : Array.isArray(preSelectedResourceIds) &&
                preSelectedResourceIds.length > 0
                ? preSelectedResourceIds
                : selectedResources
                  .map((r: any) => r.id || r.resource_id || "")
                  .filter((id: string) => id !== "");
          if (Array.isArray(savedIds) && savedIds.length > 0)
            setSavedPreSelectedResourceIds(savedIds as string[]);

          setPreSelectedResourceIds([]);
          setSelectedResourcesCount(0);
          setCompleteFilterData((prev: any) => {
            const copy = { ...prev };
            if (copy.resources_list) delete copy.resources_list;
            return copy;
          });
        } else {
          if (
            Array.isArray(preSelectedResourceIds) &&
            preSelectedResourceIds.length === 0 &&
            Array.isArray(savedPreSelectedResourceIds) &&
            savedPreSelectedResourceIds.length > 0
          ) {
            setPreSelectedResourceIds(savedPreSelectedResourceIds);
            setSelectedResourcesCount(savedPreSelectedResourceIds.length);
          }
        }
      }
    };

    useEffect(() => {
      // Cloud-only user
      if (isOnlyCloudUser) {
        handleInputChange("orgPostureScope", "cloud");
        return;
      }

      // On-prem-only user
      if (isOnlyPremAssetUser) {
        handleInputChange("orgPostureScope", "on_prem");
        return;
      }

      // Both cloud & on-prem user
      if (isBothCloudAndPremAssetUser) {
        handleInputChange("orgPostureScope", "all"); // default for both
      }
    }, [
      isOnlyCloudUser,
      isOnlyPremAssetUser,
      isBothCloudAndPremAssetUser,
    ]);


    const handleSelectAssets = () => {
      setAssetsLoader((prev) => !prev);
    };

    const handleSelectResources = () => {
      setResourcesLoader((prev) => !prev);
    };

    const handleFilterDataSaved = (filterData: any, fullTreeData?: any) => {
      setCompleteFilterData(filterData);

      if (selectedReportType?.name === "Organization Posture report") {
        processFilterDataForPosture(filterData, fullTreeData);
      }

      // Handle assets if devices_list exists
      if (filterData.devices_list) {
        const ids = filterData.devices_list.map((asset: any) => asset.id);
        setPreSelectedAssetIds(ids);
        setSavedPreSelectedAssetIds(ids);
        setSelectedAssetsCount(filterData.devices_list.length);
      }

      // Handle resources if resources_list exists
      if (filterData.resources_list) {
        const ids = filterData.resources_list.map(
          (resource: any) => resource.id
        );
        setPreSelectedResourceIds(ids);
        setSavedPreSelectedResourceIds(ids);
        setSelectedResourcesCount(filterData.resources_list.length);
      }
    };

    // const handleSelectUsers = () => {
    //     setUsersLoader((prev) => !prev);
    // };

    const handleUsersSelected = (users: any[]) => {
      setSelectedUsers(users || []);
      setUsersLoader(false);
    };

    // Fetch compliance filters data (frameworkOn: defaults only for frameworks switched on)
    const fetchComplianceFilters = async (frameworkOn: (name: string) => boolean) => {
      try {
        const response = await axiosInstance.get(
          `${END_POINTS.COMPLIANCE_FILTERS}`
        );
        if (response.status === 200 && response.data && response.data.data) {
          const controlTypes = response.data.data.control_types || [];

          // Transform control types to compliance options format with versions
          const options: any[] = [];

          controlTypes.forEach((controlType: any) => {
            const controlName = controlType.name || controlType.id;

            if (controlType.versions && controlType.versions.length > 0) {
              // Create options for each version
              controlType.versions.forEach((version: any) => {
                // Format: controlName_version - preserve spaces in control name (e.g., "PCI DSS_4.0.1")
                options.push({
                  value: `${controlName}_${version.version}`,
                  label: `${controlName} ${version.version}`,
                  controlType: controlName,
                  version: version.version,
                  versionId: version.id,
                  controlTypeId: controlType.id,
                });
              });
            } else {
              // If no versions, create option with just control type name
              options.push({
                value: controlName.toUpperCase(),
                label: controlName,
                controlType: controlName,
                version: null,
                versionId: null,
                controlTypeId: controlType.id,
              });
            }
          });

          // Use only actual framework options; remove the artificial "All" option
          setComplianceOptions(options);

          // Set default selections to specified control types
          const defaultSelections: string[] = [];

          // Define the default control types to select (frameworks switched on only)
          const defaultControlTypes = [
            { name: "NIST", version: "800-171 r2" },
            { name: "NIST", version: "800-53 r5" },
            { name: "CIS", version: "8" },
            { name: "PCI DSS", version: "4.0.1" },
            { name: "SPARK", version: "1" },
          ].filter((ctrl) => frameworkOn(ctrl.name));

          // Find and add default selections
          defaultControlTypes.forEach((defaultCtrl) => {
            const found = options.find(
              (opt) =>
                (opt.controlType === defaultCtrl.name || opt.controlType?.toUpperCase() === defaultCtrl.name?.toUpperCase()) &&
                (!defaultCtrl.version || opt.version === defaultCtrl.version || opt.value === `${defaultCtrl.name}_${defaultCtrl.version}` || opt.value === "SPARK_1")
            );
            if (found) {
              defaultSelections.push(found.value);
            }
          });

          // Set default selections if found, otherwise set to first option
          if (defaultSelections.length > 0) {
            setFormData((prev) => ({
              ...prev,
              assetCompliance: defaultSelections,
            }));
          } else if (options.length > 0) {
            setFormData((prev) => ({
              ...prev,
              assetCompliance: [options[0].value],
            }));
          }
        }
      } catch (error: any) {
        console.error("Failed to fetch compliance filters:", error);
        // Set empty options if API fails
        setComplianceOptions([]);
      }
    };

    // Fetch resource compliance filters data (frameworkOn: defaults only for frameworks switched on)
    const fetchResourceComplianceFilters = async (frameworkOn: (name: string) => boolean) => {
      try {
        const response = await axiosInstance.get(
          `${END_POINTS.COMPLIANCE_FILTERS}`
        );
        if (response.status === 200 && response.data && response.data.data) {
          const controlTypes = response.data.data.control_types || [];

          // Transform control types to resource compliance options format with versions
          const options: any[] = [];

          controlTypes.forEach((controlType: any) => {
            const controlName = controlType.name || controlType.id;

            if (controlType.versions && controlType.versions.length > 0) {
              // Create options for each version
              controlType.versions.forEach((version: any) => {
                // Format: controlName_version - preserve spaces in control name (e.g., "PCI DSS_4.0.1")
                options.push({
                  value: `${controlName}_${version.version}`,
                  label: `${controlName} ${version.version}`,
                  controlType: controlName,
                  version: version.version,
                  versionId: version.id,
                  controlTypeId: controlType.id,
                });
              });
            } else {
              // If no versions, create option with just control type name
              options.push({
                value: controlName.toUpperCase(),
                label: controlName,
                controlType: controlName,
                version: null,
                versionId: null,
                controlTypeId: controlType.id,
              });
            }
          });

          // Use only actual framework options; remove the artificial "All" option
          setResourceComplianceOptions(options);

          // Set default selections to specified control types
          const defaultSelections: string[] = [];

          // Define the default control types to select (frameworks switched on only)
          const defaultControlTypes = [
            { name: "NIST", version: "800-171 r2" },
            { name: "NIST", version: "800-53 r5" },
            { name: "CIS", version: "8" },
            { name: "PCI DSS", version: "4.0.1" },
            { name: "SPARK", version: "1" },
          ].filter((ctrl) => frameworkOn(ctrl.name));

          // Find and add default selections (note: resource compliance uses uppercase values)
          defaultControlTypes.forEach((defaultCtrl) => {
            const found = options.find(
              (opt) =>
                (opt.controlType === defaultCtrl.name || opt.controlType?.toUpperCase() === defaultCtrl.name?.toUpperCase()) &&
                (!defaultCtrl.version || opt.version === defaultCtrl.version || opt.value === `${defaultCtrl.name}_${defaultCtrl.version}` || opt.value === "SPARK_1")
            );
            if (found) {
              defaultSelections.push(found.value);
            }
          });

          // Set default selections if found, otherwise use first option
          if (defaultSelections.length > 0) {
            setFormData((prev) => ({
              ...prev,
              resourceCompliance: defaultSelections,
            }));
          } else if (options.length > 0) {
            setFormData((prev) => ({
              ...prev,
              resourceCompliance: [options[0].value], // Fallback to first available option
            }));
          }
        }
      } catch (error: any) {
        console.error("Failed to fetch resource compliance filters:", error);
        // Set empty options if API fails
        setResourceComplianceOptions([]);
      }
    };

    // Fetch schedule frequency data
    const fetchScheduleFrequency = async () => {
      try {
        setScheduleLoading(true);
        const response = await axiosInstance.get(
          `${END_POINTS.SCHEDULE_FREQUENCY}?type=report`
        );
        if (response.status === 200 && response.data) {
          // Use API response data directly
          const apiFrequencyData = response.data.data?.frequency || [];

          setScheduleOptions({
            frequency: apiFrequencyData,
            Days: response.data.data?.weekly || [],
          });
        }
      } catch (error: any) {
        console.error("Failed to fetch schedule frequency:", error);
        toast.error("Failed to load schedule options");
      } finally {
        setScheduleLoading(false);
      }
    };

    // Handle schedule button click
    const handleScheduleClick = () => {
      fetchScheduleFrequency();
      setScheduleDrawerOpen(true);
    };

    const fetchAssetsData = async () => {
      try {
        setIsLoading(true);

        // Mock device data for testing
        // const mockDeviceData = [
        //     { id: "device_001", device_name: "Windows Server 2019", device_type: "Server", vendor: "Microsoft", model: "Windows Server" },
        //     { id: "device_002", device_name: "Ubuntu Web Server", device_type: "Server", vendor: "Canonical", model: "Ubuntu 20.04" },
        //     { id: "device_003", device_name: "Cisco Router R1", device_type: "Network", vendor: "Cisco", model: "ISR4331" },
        //     { id: "device_004", device_name: "Database Server DB1", device_type: "Database", vendor: "Oracle", model: "Oracle Linux" },
        //     { id: "device_005", device_name: "Firewall FW-01", device_type: "Security", vendor: "Fortinet", model: "FortiGate 100F" },
        //     { id: "device_006", device_name: "Load Balancer LB1", device_type: "Network", vendor: "F5", model: "BIG-IP" },
        //     { id: "device_007", device_name: "Mail Server MS1", device_type: "Mail", vendor: "Microsoft", model: "Exchange Server" },
        //     { id: "device_008", device_name: "File Server FS1", device_type: "Storage", vendor: "Dell", model: "PowerEdge" },
        //     { id: "device_009", device_name: "Backup Server BS1", device_type: "Backup", vendor: "Veeam", model: "Veeam Backup" },
        //     { id: "device_010", device_name: "Monitoring Server MON1", device_type: "Monitoring", vendor: "Zabbix", model: "Zabbix Server" }
        // ];
        // setAssetData(mockDeviceData);

        // Uncomment below to try real API first
        try {
          const endpoint = END_POINTS.DEVICE_LIST;
          const payload = {};
          const response = await axiosInstance.post(endpoint, payload);
          if (response.data && response.data?.data?.device_list?.length > 0) {
            setAssetData(response.data?.data?.device_list || []);
          }
        } catch (error) {
          console.error("Error in fetchAssetsData:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Fetch cloud resources data for Resource Report
    const fetchCloudResourcesData = async () => {
      try {
        setIsLoading(true);
        const endpoint = END_POINTS.DEVICE_LIST_CLOUD;
        const payload = { type: 1 };

        const response = await axiosInstance.post(endpoint, payload);
        if (response.data && response.data?.data?.cloud_resource) {
          const cloudResources = response.data?.data?.cloud_resource || [];
          const filteredResources = cloudResources.filter(
            (resource: any) => resource?.last_scan_status === true
          );
          setCloudResourceData(filteredResources);
        } else {
          setCloudResourceData([]);
        }
      } catch (error) {
        console.error("Error fetching cloud resources data:", error);
        toast.error("Failed to fetch cloud resources");
        setCloudResourceData([]);
      } finally {
        setIsLoading(false);
      }
    };

    const handleDateChange = (newDate: any) => {
      setSelectedDate(newDate);

      // Set current time + 5 minutes when date is selected (only for non-hourly frequencies)
      if (newDate && !isHourly()) {
        const now = new Date();
        // Add 5 minutes to current time
        const futureTime = new Date(now.getTime() + 5 * 60 * 1000);
        const currentHour = futureTime.getHours();
        const currentMinute = futureTime.getMinutes();

        // Convert 24-hour format to 12-hour format with AM/PM
        let hour12 = currentHour;
        let amPm = "AM";

        if (currentHour === 0) {
          hour12 = 12;
          amPm = "AM";
        } else if (currentHour < 12) {
          hour12 = currentHour;
          amPm = "AM";
        } else if (currentHour === 12) {
          hour12 = 12;
          amPm = "PM";
        } else {
          hour12 = currentHour - 12;
          amPm = "PM";
        }

        // Set current time + 5 minutes values
        setSelectedHour(hour12.toString());
        setSelectedMinute(currentMinute.toString().padStart(2, "0"));
        setSelectedAmPm(amPm);

        // Update the combined time string for One Time, Daily, and Monthly frequencies
        const timeString = `${hour12}:${currentMinute
          .toString()
          .padStart(2, "0")} ${amPm}`;
        setSelectedTime(timeString);
      }
    };

    // Handle Day Change (Only for weekly frequency)
    const handleDayChange = (event: any) => {
      setSelectedDay(event.target.value);
    };

    // Handle Hour Change
    const handleHourChange = (event: any) => {
      setSelectedHour(event.target.value);
      // Update the combined time string
      const timeString = `${event.target.value}:${selectedMinute} ${selectedAmPm}`;
      setSelectedTime(timeString);
    };

    // Handle Minute Change
    const handleMinuteChange = (event: any) => {
      setSelectedMinute(event.target.value);
      // Update the combined time string
      const timeString = `${selectedHour}:${event.target.value} ${selectedAmPm}`;
      setSelectedTime(timeString);
    };

    // Handle AM/PM Change
    const handleAmPmChange = (event: any) => {
      const newAmPm = event.target.value;
      setSelectedAmPm(newAmPm);
      // Update the combined time string
      if (isHourly()) {
        const hr = hourlyInterval || selectedHour;
        const min = hourlyMinute || selectedMinute;
        if (hr && min) {
          setSelectedTime(`${hr}:${min} ${newAmPm}`);
        } else if (hr) {
          setSelectedTime(`${hr}:00 ${newAmPm}`);
        }
      } else {
        const timeString = `${selectedHour}:${selectedMinute} ${newAmPm}`;
        setSelectedTime(timeString);
      }
    };

    // Handle Hourly Interval Change
    const handleHourlyIntervalChange = (event: any) => {
      const newInterval = event.target.value;
      setHourlyInterval(newInterval);
      setSelectedHour(newInterval);
      const min = hourlyMinute || selectedMinute || "00";
      const amPm = selectedAmPm || "AM";
      if (newInterval) {
        setSelectedTime(`${newInterval}:${min} ${amPm}`);
      }
    };

    // Handle Hourly Minute Change
    const handleHourlyMinuteChange = (event: any) => {
      const newMinute = event.target.value;
      setHourlyMinute(newMinute);
      setSelectedMinute(newMinute);
      const hr = hourlyInterval || selectedHour;
      const amPm = selectedAmPm || "AM";
      if (hr) {
        setSelectedTime(`${hr}:${newMinute} ${amPm}`);
      }
    };

    const getUtcDateTime = (dateObj: any, timeStr: string) => {
      if (!timeStr) return { date: dateObj ? dateObj.format("YYYY-MM-DD") : null, time: null };
      const dateString = dateObj ? dateObj.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
      const dateTimeString = `${dateString} ${timeStr}`;
      const utcMoment = dayjs(dateTimeString, "YYYY-MM-DD hh:mm A").utc();
      return {
        date: dateObj ? utcMoment.format("YYYY-MM-DD") : null,
        time: utcMoment.format("HH:mm")
      };
    };

    // Function to build update payload
    const buildUpdatePayload = (
      is_scheduled: boolean = false,
      is_download: boolean = false,
      scheduleData?: any
    ) => {
      const toBool = (v: string) => (v || "").toLowerCase() === "include";
      // let assetSelection = formData.assets;
      // let resourceSelection = formData.resource;
      // let userSelection = formData.user;
      // let customise_assets = {};
      let customise_users = {};
      let assetIdsArray: string[] = [];
      let resourceIdsArray: string[] = [];

      // Handle asset_ids based on assets dropdown selection
      if (selectedReportType?.name === "Asset Report") {
        // For Asset Report, formData.assets contains the selected asset ID directly
        if (formData.assets && formData.assets !== "") {
          assetIdsArray = [formData.assets];
        } else {
          assetIdsArray = [];
        }
      } else if (formData.assets === "2") {
        // For other report types, "2" means Customise
        // Set asset_ids to array of selected asset IDs from devices_list
        if (
          Object.keys(completeFilterData).length > 0 &&
          completeFilterData.devices_list
        ) {
          assetIdsArray = completeFilterData.devices_list
            .map((asset: any) => asset.id || asset.asset_id || "")
            .filter((id: string) => id !== "");
        } else {
          // Fallbacks when no new selection was saved from the drawer
          // 1) Use preSelectedAssetIds from edit mode if available
          if (
            Array.isArray(preSelectedAssetIds) &&
            preSelectedAssetIds.length > 0
          ) {
            assetIdsArray = preSelectedAssetIds.filter(
              (id: string) => id !== ""
            );
          } else {
            // 2) Otherwise, fallback to locally selectedAssets
            assetIdsArray = selectedAssets
              .map((asset: any) => asset.id || asset.asset_id || "")
              .filter((id: string) => id !== "");
          }
        }
      } else {
        // For "0" (All Assets) and "1" (As per global filter), asset_ids should be empty array
        assetIdsArray = [];
      }

      // Handle resource_ids based on resource dropdown selection
      if (selectedReportType?.name === "Resource Report") {
        // For Resource Report, formData.resource contains the selected resource ID directly
        if (formData.resource && formData.resource !== "") {
          resourceIdsArray = [formData.resource];
        } else {
          resourceIdsArray = [];
        }
      } else if (formData.resource === "2") {
        // For Resource Summary Report, "2" means Customise
        // Set resource_ids to array of selected resource IDs from resources_list
        if (
          Object.keys(completeFilterData).length > 0 &&
          completeFilterData.resources_list
        ) {
          resourceIdsArray = completeFilterData.resources_list
            .map((resource: any) => resource.id || resource.resource_id || "")
            .filter((id: string) => id !== "");
        } else {
          // Fallbacks when no new selection was saved from the drawer
          if (
            Array.isArray(preSelectedResourceIds) &&
            preSelectedResourceIds.length > 0
          ) {
            resourceIdsArray = preSelectedResourceIds.filter(
              (id: string) => id !== ""
            );
          } else {
            // Otherwise, fallback to locally selectedResources
            resourceIdsArray = selectedResources
              .map((resource: any) => resource.id || resource.resource_id || "")
              .filter((id: string) => id !== "");
          }
        }
      }

      // Handle user_ids based on user dropdown selection and selectedUsers
      let finalUserIds: any[] = [];
      if (formData.user === "2") {
        // For "Customise", use selectedUsers array and structure based on user type
        finalUserIds = (selectedUsers || []).map((u: any) => {
          const userId = u.id || u.user_id || "";
          const userEmail = u.organization_email || u.email || "";

          // Structure for external users (has email) vs internal users
          if (userEmail && userEmail.trim() !== "") {
            // External user - include email
            return {
              email: userEmail,
            };
          } else {
            // Internal user - only user_id
            return {
              user_id: userId,
              email: "",
            };
          }
        });

        // Populate customise_users with the structured user data
        customise_users = {
          user_ids: finalUserIds,
        };
      }

      // Build scheduling parameters
      let scheduleParams: any = {};
      if (selectedFrequency) {
        // Get the frequency object from API response
        const frequencyObj = scheduleOptions?.frequency?.find(
          (f: any) => f.id === selectedFrequency
        );

        // Use the UUID frequency ID from the API response
        scheduleParams.frequency_id = frequencyObj?.id || selectedFrequency;

        // Add day for weekly frequency
        if (frequencyObj?.name === "Weekly" && selectedDay && selectedTime) {
          scheduleParams.day = selectedDay; // selectedDay is already in lowercase format
          scheduleParams.time = selectedTime;
        }

        if (
          (frequencyObj?.name === "One Time" ||
            frequencyObj?.name === "Daily" ||
            frequencyObj?.name === "Monthly") &&
          selectedDate
        ) {
          scheduleParams.date = selectedDate;
          scheduleParams.time = selectedTime;
        }

        if (frequencyObj?.name === "Hourly") {
          const hr = hourlyInterval || selectedHour;
          const min = hourlyMinute || selectedMinute || "00";
          const amPm = selectedAmPm || "AM";
          const timeStr = (hr && min) ? `${hr}:${min} ${amPm}` : selectedTime;
          scheduleParams.time = timeStr;
          scheduleParams.date = null;
        }
      }

      // Determine which IDs to use based on report type
      let finalIdsArray: string[] = [];
      if (
        selectedReportType?.name === "Asset Report" ||
        selectedReportType?.name === "Asset Summary Report" ||
        selectedReportType?.name === "Organization Summary Report" ||
        selectedReportType?.name === "Organization CVE Report" ||
        selectedReportType?.name === "Organization Compliance Report" ||
        selectedReportType?.name === "Organization Posture report"
      ) {
        finalIdsArray = assetIdsArray;
      } else if (
        selectedReportType?.name === "Resource Report" ||
        selectedReportType?.name === "Resource Summary Report"
      ) {
        finalIdsArray = resourceIdsArray;
      }

      // Build report_metadata conditionally based on selected report type
      const metadata: any = {};

      const reportName = selectedReportType?.name || selectedReportType;

      // Organization posture only
      if (reportName === "Organization Posture report") {
        metadata.org_posture = {
          security_posture: (formData.orgPostureScope).toLowerCase(),
          on_prem_risks_accepted:
            (formData.orgOnPremRiskAccepted || "Exclude").toLowerCase() ===
            "include",
        };
      }

      // Asset-focused reports
      if (
        reportName === "Asset Report" ||
        reportName === "Asset Summary Report" ||
        reportName === "Organization Summary Report"
      ) {
        metadata.asset_posture = {
          benchmarks: Number(formData.benchmarks),
          recommendations: {
            passed:
              formData.recommendation === "0" ||
              formData.recommendation === "2",
            failed:
              formData.recommendation === "1" ||
              formData.recommendation === "2",
            all: formData.recommendation === "2",
          },
          risk_accepted: toBool(formData.riskAccepted),
          description: toBool(formData.descriptionInclude),
          remediation: toBool(formData.remediation),
          impact: toBool(formData.impact),
          log_output: formData.logOutput.toLowerCase() !== "Exclude",
        };
      }

      // Asset CVE specific
      if (
        reportName === "Asset Report" ||
        reportName === "Organization CVE Report"
      ) {
        metadata.asset_cve = {
          cve_description: toBool(formData.cveDescription),
        };
      }

      // Asset compliance for asset/org compliance/summary
      if (
        reportName === "Asset Report" ||
        reportName === "Asset Summary Report" ||
        reportName === "Organization Summary Report" ||
        reportName === "Organization Compliance Report"
      ) {
        metadata.asset_compliance = {
          frameworks: formData.assetCompliance,
          compliance_description: toBool(formData.complianceDescription),
        };
      }

      // Resource-focused reports
      if (
        reportName === "Resource Report" ||
        reportName === "Resource Summary Report"
      ) {
        // Dynamic recommendations based on selected value
        let recommendations = {
          passed: false,
          failed: false,
          all: false,
        };

        if (formData.recommendation === "0") {
          // Passed selected: passed = true, others = false
          recommendations.passed = true;
          recommendations.failed = false;
          recommendations.all = false;
        } else if (formData.recommendation === "1") {
          // Failed selected: failed = true, others = false
          recommendations.passed = false;
          recommendations.failed = true;
          recommendations.all = false;
        } else if (formData.recommendation === "2") {
          // All selected: all = true, others = true
          recommendations.passed = true;
          recommendations.failed = true;
          recommendations.all = true;
        }

        metadata.resource_compliance = {
          frameworks: formData.resourceCompliance,
          compliance_description: toBool(formData.complianceDescription),
          recommendations: recommendations,
        };
      }

      // MITRE sections only where applicable
      if (
        reportName === "Asset Report" ||
        reportName === "Asset Summary Report" ||
        reportName === "Organization Summary Report"
      ) {
        metadata.asset_mitre = {
          include: toBool(formData.assetMitre),
          mitre_description: toBool(formData.mitreDescription),
        };
      }

      if (
        reportName === "Resource Report" ||
        reportName === "Resource Summary Report"
      ) {
        metadata.resource_mitre = {
          include: toBool(formData.resourceMitre),
          mitre_description: toBool(formData.resourceMitreDescription),
        };
      }

      let module = "";

      if (reportName === "Organization Posture report") {
        if (formData.orgPostureScope === "on_prem") {
          module = "on_premise";
        } else if (formData.orgPostureScope === "cloud") {
          module = "cloud";
        } else {
          module = "";
        }
      } else if (
        reportName === "Asset Report" ||
        reportName === "Asset Summary Report" ||
        reportName === "Organization Summary Report" ||
        reportName === "Organization CVE Report" ||
        reportName === "Organization Compliance Report"
      ) {
        module = "on_premise";
      } else if (
        reportName === "Resource Report" ||
        reportName === "Resource Summary Report"
      ) {
        module = "cloud";
      }

      let payload = {
        report_id: reportData?.id || reportData?.report_id,
        report_format: formData.reportFormat.toLowerCase(),
        type:
          selectedReportType?.name === "Organization Compliance Report"
            ? "org_compliance"
            : selectedReportType?.value || selectedReportType,
        report_name: formData.reportName,
        description: formData.description,
        is_scheduled: is_scheduled,
        is_download: is_download,
        report_metadata: metadata,
        asset_ids:
          selectedReportType?.name === "Asset Report"
            ? "2"
            : selectedReportType?.name === "Resource Report"
              ? "2"
              : selectedReportType?.name === "Resource Summary Report"
                ? formData.resource
                : formData.assets,
        customise_asset_ids:
          selectedReportType?.name === "Resource Summary Report"
            ? resourceIdsArray
            : finalIdsArray,
        // customise_assets: customise_assets,
        customise_users: customise_users,
        date:
          scheduleData?.date !== undefined
            ? scheduleData.date
            : isHourly()
            ? null
            : (selectedDate || selectedTime ? getUtcDateTime(selectedDate, selectedTime).date : null),
        time:
          scheduleData?.time ??
          (selectedTime
            ? getUtcDateTime(selectedDate, selectedTime).time
            : hourlyInterval
            ? getUtcDateTime(
                null,
                `${hourlyInterval}:${hourlyMinute || "00"} ${selectedAmPm || "AM"}`
              ).time
            : null),
        day:
          scheduleData?.day?.toLowerCase() ??
          (selectedDay ? selectedDay.toLowerCase() : null),
        frequency_id:
          scheduleData?.frequency_id ??
          scheduleParams.frequency_id ??
          selectedFrequency,
        frequency_name:
          scheduleOptions?.frequency?.find(
            (f: any) => f.id === selectedFrequency
          )?.name || "",
        module,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      return payload;
    };

    // Main handleSubmit function for scheduling reports
    const handleSubmit = async (
      e: React.FormEvent,
      buttonType?: "schedule" | "download"
    ) => {
      e.preventDefault();
      setProcessingButton(buttonType || null);
      setIsLoading(true);

      try {
        // If in edit mode, call the update callback instead
        if (isEditMode && onScheduleUpdate) {
          const effectiveTime =
            selectedTime ||
            (isHourly() && hourlyInterval
              ? `${hourlyInterval}:${hourlyMinute || "00"} ${selectedAmPm || "AM"}`
              : "");
          const scheduleData = {
            is_scheduled:
              buttonType === "schedule" || buttonType === "download", // true for both buttons
            is_download: buttonType === "download", // true only for "Schedule & Download"
            frequency_id: selectedFrequency,
            date:
              isHourly()
                ? null
                : (selectedDate || effectiveTime)
                ? getUtcDateTime(selectedDate, effectiveTime).date || ""
                : "",
            time: effectiveTime ? getUtcDateTime(selectedDate, effectiveTime).time || "" : "",
            day: selectedDay,
          };

          onScheduleUpdate(scheduleData);
          setScheduleDrawerOpen(false);
          setProcessingButton(null);
          setIsLoading(false);
          return;
        }

        // Determine is_scheduled and is_download based on button type
        const is_scheduled =
          buttonType === "schedule" || buttonType === "download";
        const is_download = buttonType === "download";

        // let assetSelection = formData.assets;
        // let resourceSelection = formData.resource;
        // let userSelection = formData.user;
        // let customise_assets = {};

        const payload = buildUpdatePayload(is_scheduled, is_download);
        console.log(payload, "payload");

        if (buttonType === "download") {
          // Use REPORT_DOWNLOAD endpoint for downloading
          const response = await axiosInstance.post(
            END_POINTS.REPORT_DOWNLOAD,
            payload
          );
          if (response.status === 200) {
            toast.success("Report downloaded successfully !");
            onRefreshReports?.();
            setScheduleDrawerOpen(false);
          }
          resetFormData();
        } else {
          // Use REPORT_DOWNLOAD endpoint for scheduling (same endpoint but different payload structure)
          const response = await axiosInstance.post(
            END_POINTS.REPORT_DOWNLOAD,
            payload
          );
          if (response.status === 200) {
            toast.success("Report generated successfully !");
            onRefreshReports?.();
            setScheduleDrawerOpen(false);
          }
          resetFormData();
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to schedule report"
        );
      } finally {
        setProcessingButton(null);
        setIsLoading(false);
      }
    };

    // Expose the buildUpdatePayload function through the ref
    useImperativeHandle(ref, () => ({
      buildPayload: () => {
        // This can be used for other purposes if needed
        return {};
      },
      buildUpdatePayload: (
        is_scheduled: boolean = false,
        is_download: boolean = false,
        scheduleData?: any
      ) => buildUpdatePayload(is_scheduled, is_download, scheduleData),
    }));

    // Fetch compliance filters on component mount and when report type changes
    useEffect(() => {
      fetchComplianceFilters(isFrameworkOn);
      fetchResourceComplianceFilters(isFrameworkOn);
      // Fetch asset data for Asset Report
      if (selectedReportType?.name === "Asset Report") {
        fetchAssetsData();
      }
      // Fetch cloud resources data for Resource Report
      if (selectedReportType?.name === "Resource Report") {
        fetchCloudResourcesData();
      }
    }, [selectedReportType, isFrameworkOn]);

    // Listen for global filter save events and re-fetch filter data for Organization Posture report
    // This handles the case where the standard global filter (top-right icon) is used.
    // The standard global filter uses skipApiCall=false, so it saves via API and emits 'filterSaved'.
    // The onFilterDataSaved callback is only triggered for skipApiCall=true flows.
    useEffect(() => {
      if (selectedReportType?.name !== "Organization Posture report") return;

      const handleFilterSaved = async () => {
        try {
          const filterResponse = await axiosInstance.get(END_POINTS.GLOBAL_DASHBOARD_REGION);
          const filterData = filterResponse?.data?.data || {};
          processFilterDataForPosture(filterData);
        } catch (e) {
          console.error("Failed to re-fetch global filter data after save", e);
        }
      };

      eventEmitter.on('filterSaved', handleFilterSaved);
      return () => {
        eventEmitter.off('filterSaved', handleFilterSaved);
      };
    }, [selectedReportType?.name]);

    // Populate form data when reportData or selectedReportType changes
    useEffect(() => {
      // If we have reportData (editing existing report), populate from reportData
      if (reportData) {
        const toTitleCase = (v: string) =>
          (v || "").toString().trim().toLowerCase() === "pdf" ? "PDF" : "Excel";
        const toIncludeExclude = (v: any) => {
          if (typeof v === "boolean") return v ? "Include" : "Exclude";
          const s = (v || "").toString().trim().toLowerCase();
          if (s === "include") return "Include";
          if (s === "exclude") return "Exclude";
          return "Include";
        };

        const normalizedReportFormat = (() => {
          const rf = reportData.report_format || reportData.format;
          if (!rf) return "Excel";
          return toTitleCase(rf);
        })();

        const assetCount =
          reportData.asset_ids && reportData.asset_filter === "2"
            ? reportData.asset_ids.length
            : 0;
        setSelectedAssetsCount(assetCount);

        // For Resource Report and Resource Summary Report, use asset_ids for resource count
        const resourceCount =
          reportData.asset_ids && reportData.asset_filter === "2"
            ? reportData.asset_ids.length
            : 0;
        setSelectedResourcesCount(resourceCount);

        // For Asset Report, set the assets field to the selected asset ID
        const assetValue =
          selectedReportType?.name === "Asset Report" &&
            reportData.asset_ids &&
            reportData.asset_ids.length > 0
            ? reportData.asset_ids[0]
            : reportData.asset_filter || "";

        const resourceValue =
          selectedReportType?.name === "Resource Report" &&
            reportData.asset_ids &&
            reportData.asset_ids.length > 0
            ? reportData.asset_ids[0]
            : reportData.asset_filter || "";

        // Process asset compliance frameworks to ensure they match available options
        const processAssetCompliance = (frameworks: string[]) => {
          if (!frameworks || frameworks.length === 0) return [];

          // If complianceOptions are loaded, filter frameworks to only include available ones
          if (complianceOptions.length > 0) {
            const availableOptions = complianceOptions.map((opt) => opt.value);
            return frameworks.filter((framework) =>
              availableOptions.includes(framework)
            );
          }

          // If complianceOptions not loaded yet, return frameworks as-is
          return frameworks;
        };

        // Process resource compliance frameworks similarly
        const processResourceCompliance = (frameworks: string[]) => {
          if (!frameworks || frameworks.length === 0) return [];

          // If resourceComplianceOptions are loaded, filter frameworks to only include available ones
          if (resourceComplianceOptions.length > 0) {
            const availableOptions = resourceComplianceOptions.map(
              (opt) => opt.value
            );
            return frameworks.filter((framework) =>
              availableOptions.includes(framework)
            );
          }

          // If resourceComplianceOptions not loaded yet, return frameworks as-is
          return frameworks;
        };

        setFormData({
          reportName: reportData.custom_report_name || "",
          description: reportData.description || "",
          reportFormat: normalizedReportFormat,
          logOutput: toIncludeExclude(
            reportData.report_metadata?.asset_posture?.log_output
          ),
          remediation: toIncludeExclude(
            reportData.report_metadata?.asset_posture?.remediation
          ),
          riskAccepted: toIncludeExclude(
            reportData.report_metadata?.asset_posture?.risk_accepted
          ),
          recommendation: (() => {
            // Check recommendations based on report type
            let recommendations;
            if (
              selectedReportType?.name === "Resource Report" ||
              selectedReportType?.name === "Resource Summary Report"
            ) {
              // For Resource Reports, recommendations are under resource_compliance
              recommendations =
                reportData.report_metadata?.resource_compliance?.recommendations;
            } else {
              // For Asset Reports, recommendations are under asset_posture
              recommendations =
                reportData.report_metadata?.asset_posture?.recommendations;
            }
            if (recommendations?.all === true) {
              return "2"; // "All" radio button value
            } else if (recommendations?.failed === true) {
              return "1"; // "Failed" radio button value
            } else if (recommendations?.passed === true) {
              return "0"; // "Passed" radio button value
            }
            return "1"; // Default to "Failed"
          })(),
          assetCompliance: processAssetCompliance(
            reportData.report_metadata?.asset_compliance?.frameworks || []
          ),
          resourceCompliance: processResourceCompliance(
            reportData.report_metadata?.resource_compliance?.frameworks || []
          ),
          assets: assetValue,
          resource: resourceValue,
          user: reportData.user || reportData.user_name || "",
          benchmarks:
            reportData.report_metadata?.asset_posture?.benchmarks !== undefined
              ? reportData.report_metadata?.asset_posture?.benchmarks.toString()
              : "",
          cveDescription: toIncludeExclude(
            reportData.report_metadata?.asset_cve?.cve_description
          ),
          complianceDescription: toIncludeExclude(
            reportData.report_metadata?.asset_compliance?.compliance_description
          ),
          mitreDescription: toIncludeExclude(
            reportData.report_metadata?.asset_mitre?.mitre_description
          ),
          assetMitre: toIncludeExclude(
            reportData.report_metadata?.asset_mitre?.include
          ),
          resourceMitre: toIncludeExclude(
            reportData.report_metadata?.resource_mitre?.include
          ),
          resourceMitreDescription: toIncludeExclude(
            reportData.report_metadata?.resource_mitre?.mitre_description
          ),
          impact: toIncludeExclude(
            reportData.report_metadata?.asset_posture?.impact
          ),
          descriptionInclude: toIncludeExclude(
            reportData.report_metadata?.asset_posture?.description
          ),
          selectedAssetsCount: assetCount,
          selectedResourcesCount: resourceCount,
          orgPostureScope: (() => {
            const scope = reportData.report_metadata?.org_posture?.security_posture;
            const s = String(scope).toLowerCase();
            if (s === "on-prem" || s === "on_prem") return "on_prem";
            if (s === "cloud") return "cloud";
            return "all";
          })(),
          orgOnPremRiskAccepted: reportData.report_metadata?.org_posture
            ?.on_prem_risks_accepted
            ? "Include"
            : "Exclude",
        });

        // Pre-populate selected users if available
        if (Array.isArray(reportData.user_ids)) {
          setSelectedUsers(reportData.user_ids);
        }

        // Pre-populate asset IDs if available and asset_filter is "2" (Customise)
        if (
          reportData.asset_filter === "2" &&
          Array.isArray(reportData.asset_ids)
        ) {
          setPreSelectedAssetIds(reportData.asset_ids);
          setSavedPreSelectedAssetIds(reportData.asset_ids);
        } else {
          setPreSelectedAssetIds([]);
          setSavedPreSelectedAssetIds([]);
        }

        // Pre-populate resource IDs if available (for resource reports)
        if (
          reportData.asset_filter === "2" &&
          Array.isArray(reportData.asset_ids)
        ) {
          setPreSelectedResourceIds(reportData.asset_ids);
          setSavedPreSelectedResourceIds(reportData.asset_ids);
        } else {
          setPreSelectedResourceIds([]);
          setSavedPreSelectedResourceIds([]);
        }

        // Pre-populate schedule data if available from scheduled_report
        if (reportData.scheduled_report) {
          const scheduledReport = reportData.scheduled_report;

          if (scheduledReport.frequency) {
            setSelectedFrequency(scheduledReport.frequency);
          }
          if (scheduledReport.date) {
            setSelectedDate(dayjs(scheduledReport.date));
          }
          if (scheduledReport.day) {
            setSelectedDay(scheduledReport.day.toLowerCase());
          }
          if (scheduledReport.time) {
            // Convert time from HH:mm:ss format to local time with AM/PM format
            const timeStr = scheduledReport.time; // Format: "HH:mm:ss"
            const timeOnly = timeStr.split(":").slice(0, 2).join(":"); // Remove seconds
            const localTime = dayjs.utc(
              `2000-01-01 ${timeOnly}`,
              "YYYY-MM-DD HH:mm"
            ).local();
            const hour = localTime.hour();
            const minute = localTime.minute();

            // Convert to 12-hour format
            let hour12 = hour;
            let amPm = "AM";

            if (hour === 0) {
              hour12 = 12;
              amPm = "AM";
            } else if (hour < 12) {
              hour12 = hour;
              amPm = "AM";
            } else if (hour === 12) {
              hour12 = 12;
              amPm = "PM";
            } else {
              hour12 = hour - 12;
              amPm = "PM";
            }

            setSelectedHour(hour12.toString());
            setSelectedMinute(minute.toString().padStart(2, "0"));
            setSelectedAmPm(amPm);
            setHourlyInterval(hour12.toString());
            setHourlyMinute(minute.toString().padStart(2, "0"));
            setSelectedTime(
              `${hour12}:${minute.toString().padStart(2, "0")} ${amPm}`
            );
          }
        }

        // Validate pre-populated data
        const reportNameError = validateReportName(
          reportData.custom_report_name || ""
        );
        setValidationErrors({
          reportName: reportNameError,
        });
      }
    }, [
      reportData,
      selectedReportType,
      complianceOptions,
      resourceComplianceOptions,
    ]);

    // Handle compliance options updates when report data exists
    useEffect(() => {
      if (
        reportData &&
        (complianceOptions.length > 0 || resourceComplianceOptions.length > 0)
      ) {
        // Re-process compliance frameworks when options are loaded
        const processAssetCompliance = (frameworks: string[]) => {
          if (!frameworks || frameworks.length === 0) return [];

          if (complianceOptions.length > 0) {
            const availableOptions = complianceOptions.map((opt) => opt.value);
            return frameworks.filter((framework) =>
              availableOptions.includes(framework)
            );
          }

          return frameworks;
        };

        const processResourceCompliance = (frameworks: string[]) => {
          if (!frameworks || frameworks.length === 0) return [];

          if (resourceComplianceOptions.length > 0) {
            const availableOptions = resourceComplianceOptions.map(
              (opt) => opt.value
            );
            return frameworks.filter((framework) =>
              availableOptions.includes(framework)
            );
          }

          return frameworks;
        };

        setFormData((prev) => ({
          ...prev,
          assetCompliance: processAssetCompliance(
            reportData.report_metadata?.asset_compliance?.frameworks || []
          ),
          resourceCompliance: processResourceCompliance(
            reportData.report_metadata?.resource_compliance?.frameworks || []
          ),
        }));
      }
    }, [complianceOptions, resourceComplianceOptions, reportData]);

    // Automatically reset form when selectedReportType changes (but not when editing existing report)
    useEffect(() => {
      if (selectedReportType && !reportData) {
        resetFormData();
        // Set default filter selections after reset
        const name = selectedReportType.name;
        // Assets default: Global Filter for summary/organization reports
        if (
          name === "Asset Summary Report" ||
          name === "Organization Summary Report" ||
          name === "Organization CVE Report" ||
          name === "Organization Compliance Report" ||
          name === "Organization Posture report"
        ) {
          setFormData((prev) => ({ ...prev, assets: "1" })); // 1 => Global Filter
        }
        // Resource default: All for Resource Summary Report
        if (name === "Resource Summary Report") {
          setFormData((prev) => ({ ...prev, resource: "0" })); // 0 => All
        }
      }
    }, [selectedReportType]);

    // Handle triggerScheduleDrawer prop to open schedule drawer
    useEffect(() => {
      if (triggerScheduleDrawer) {
        handleScheduleClick();
        // Notify parent that the drawer has been triggered
        onScheduleDrawerTriggered?.();
      }
    }, [triggerScheduleDrawer]);

    // Map frequency from reportData to scheduleOptions after scheduleOptions is loaded
    useEffect(() => {
      if (
        reportData?.scheduled_report?.frequency &&
        scheduleOptions?.frequency?.length > 0
      ) {
        const scheduledReport = reportData.scheduled_report;

        const frequencyMap: Record<number, string> = {
          1: "One Time",
          2: "Daily",
          3: "Weekly",
          4: "Monthly",
          5: "Hourly",
        };

        const frequencyName =
          frequencyMap[scheduledReport.frequency as keyof typeof frequencyMap];

        const frequencyObj = scheduleOptions.frequency.find(
          (f: any) =>
            f.name?.toLowerCase() === frequencyName?.toLowerCase() ||
            f.id === scheduledReport.frequency
        );

        if (frequencyObj) {
          setSelectedFrequency(frequencyObj.id);
        } else {
          // Fallback: try to find by frequency number/id directly
          const frequencyById = scheduleOptions.frequency.find(
            (f: any) => f.id === scheduledReport.frequency
          );
          if (frequencyById) {
            setSelectedFrequency(frequencyById.id);
          } else {
            console.warn("Could not map frequency:", scheduledReport.frequency);
          }
        }
      }
    }, [scheduleOptions, reportData]);

    useEffect(() => {
      if (selectedReportType?.name !== "Organization Posture report") return;

      // Do not override if user already selected manually
      if (formData.orgPostureScope) return;

      if (isOnlyCloudUser) {
        handleInputChange("orgPostureScope", "cloud");
      } else if (isOnlyPremAssetUser) {
        handleInputChange("orgPostureScope", "on_prem");
      } else if (isBothCloudAndPremAssetUser) {
        handleInputChange("orgPostureScope", "all");
      }
    }, [
      selectedReportType?.name,
      isOnlyCloudUser,
      isOnlyPremAssetUser,
      isBothCloudAndPremAssetUser,
    ]);

    return (
      <>
        {/* Header - Only show when creating new report (not editing) */}
        {!reportData && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              padding: "10px",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "18px",
                fontWeight: "700",
                lineHeight: "12px",
                color: isDarkMode ? "#ffffff" : "#000000",
              }}
            >
              {selectedReportType?.name || selectedReportType}
            </Typography>
            {!hideButtons && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  onClick={() => {
                    onCancel();
                  }}
                  variant="outlined"
                  color="primary"
                  size="small"
                  sx={{
                    border: "1px solid blue",
                    width: "86px",
                    marginRight: "12px",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  startIcon={<CalendarToday />}
                  onClick={handleScheduleClick}
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={!isMainFormValid()}
                  sx={{
                    border: "1px solid blue", width: "120px", marginRight: '12px',
                    "&:disabled": {
                      bgcolor: isDarkMode ? "#424242" : "#f5f5f5",
                      color: isDarkMode ? "#666666" : "#999999",
                    },
                  }}
                >
                  Schedule
                </Button>
                <Button
                  startIcon={<Download />}
                  onClick={(e) => handleSubmit(e)}
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={!isMainFormValid()}
                  sx={{
                    border: "1px solid blue",
                    width: "120px",
                    marginRight: "12px",
                    "&:disabled": {
                      bgcolor: isDarkMode ? "#424242" : "#f5f5f5",
                      color: isDarkMode ? "#666666" : "#999999",
                    },
                  }}
                >
                  Download
                </Button>
              </Box>
            )}
          </div>
        )}

        {/* Content */}
        <div
          style={{
            padding: "12px",
            height: reportData ? "100%" : "calc(100% - 60px)",
            overflow: "auto",
          }}
          className="scroll"
        >
          {/* Report Information */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: "16px",
                fontWeight: "600",
                color: isDarkMode ? "#ffffff" : "#000000",
                mb: 2,
              }}
            >
              Basic Information
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                label="Report Name *"
                value={formData.reportName}
                onChange={(e) =>
                  handleInputChange("reportName", e.target.value)
                }
                fullWidth
                variant="outlined"
                disabled={!isEditable}
                error={!!validationErrors.reportName}
                helperText={validationErrors.reportName}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: validationErrors.reportName
                        ? "#d32f2f"
                        : "#E2E2F2",
                    },
                  },
                }}
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                fullWidth
                variant="outlined"
                disabled={!isEditable}
              />
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />

          {/* Scheduled Report Information - Show only when editing and report is scheduled */}
          {reportData &&
            reportData.is_scheduled &&
            reportData.scheduled_report && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: isDarkMode ? "#ffffff" : "#000000",
                    mb: 2,
                  }}
                >
                  Scheduled Report Information
                </Typography>
                <Box
                  sx={{
                    backgroundColor: isDarkMode ? "#2a2a2a" : "#f8f9fa",
                    padding: "16px",
                    borderRadius: "8px",
                    border: `1px solid ${isDarkMode ? "#444" : "#e0e0e0"}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "12px",
                          fontWeight: "500",
                          color: isDarkMode ? "#cccccc" : "#666666",
                          mb: 0.5,
                        }}
                      >
                        Frequency
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: "14px",
                          color: isDarkMode ? "#ffffff" : "#000000",
                          fontWeight: "500",
                        }}
                      >
                        {reportData.scheduled_report.frequency_display ||
                          (() => {
                            const frequency =
                              reportData.scheduled_report.frequency;
                            const frequencyMap: Record<number, string> = {
                              1: "One Time",
                              2: "Daily",
                              3: "Weekly",
                              4: "Monthly",
                              5: "Hourly",
                            };
                            return (
                              frequencyMap[
                                frequency as keyof typeof frequencyMap
                              ] || "Unknown"
                            );
                          })()}
                      </Typography>
                    </Box>

                    {reportData.scheduled_report.date && (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "12px",
                            fontWeight: "500",
                            color: isDarkMode ? "#cccccc" : "#666666",
                            mb: 0.5,
                          }}
                        >
                          Date
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: "14px",
                            color: isDarkMode ? "#ffffff" : "#000000",
                            fontWeight: "500",
                          }}
                        >
                          {dayjs(reportData.scheduled_report.date).format(
                            "MMM DD, YYYY"
                          )}
                        </Typography>
                      </Box>
                    )}

                    {reportData.scheduled_report.time && (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "12px",
                            fontWeight: "500",
                            color: isDarkMode ? "#cccccc" : "#666666",
                            mb: 0.5,
                          }}
                        >
                          Time
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: "14px",
                            color: isDarkMode ? "#ffffff" : "#000000",
                            fontWeight: "500",
                          }}
                        >
                          {(() => {
                            const timeStr = reportData.scheduled_report.time;
                            const timeOnly = timeStr
                              .split(":")
                              .slice(0, 2)
                              .join(":");
                            const localTime = dayjs.utc(
                              `2000-01-01 ${timeOnly}`,
                              "YYYY-MM-DD HH:mm"
                            ).local();
                            const hour = localTime.hour();
                            const minute = localTime.minute();

                            // Convert to 12-hour format
                            let hour12 = hour;
                            let amPm = "AM";

                            if (hour === 0) {
                              hour12 = 12;
                              amPm = "AM";
                            } else if (hour < 12) {
                              hour12 = hour;
                              amPm = "AM";
                            } else if (hour === 12) {
                              hour12 = 12;
                              amPm = "PM";
                            } else {
                              hour12 = hour - 12;
                              amPm = "PM";
                            }

                            return `${hour12}:${minute
                              .toString()
                              .padStart(2, "0")} ${amPm}`;
                          })()}
                        </Typography>
                      </Box>
                    )}

                    {reportData.scheduled_report.day && (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "12px",
                            fontWeight: "500",
                            color: isDarkMode ? "#cccccc" : "#666666",
                            mb: 0.5,
                          }}
                        >
                          Day
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: "14px",
                            color: isDarkMode ? "#ffffff" : "#000000",
                            fontWeight: "500",
                          }}
                        >
                          {reportData.scheduled_report.day
                            .charAt(0)
                            .toUpperCase() +
                            reportData.scheduled_report.day.slice(1)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            )}

          {/* Output Settings */}
          <Card
            sx={{ mb: 2, backgroundColor: isDarkMode ? "#2a2a2a" : "#f8f9fa" }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                cursor: "pointer",
              }}
              onClick={() =>
                setIsOutputSettingsExpanded(!isOutputSettingsExpanded)
              }
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
              >
                Output Settings
              </Typography>
              <IconButton size="small">
                {isOutputSettingsExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isOutputSettingsExpanded}>
              <Box sx={{ padding: "0 16px 16px 16px" }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 1,
                  }}
                >
                  {/* Asset Compliance - Only show for Asset Summary Report and Asset Report */}
                  {(selectedReportType?.name === "Asset Report" ||
                    selectedReportType?.name === "Asset Summary Report" ||
                    selectedReportType?.name ===
                    "Organization Summary Report") && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <FormControl fullWidth sx={{ mt: 1 }}>
                          <InputLabel
                            id="asset-compliance-label"
                            sx={{ fontSize: "12px" }}
                          >
                            Asset Compliance *
                          </InputLabel>
                          <Select
                            multiple
                            sx={{
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                              fontSize: "14px",
                              "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                                paddingTop: "8px",
                                paddingBottom: "8px",
                              },
                            }}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: "300px",
                                  overflow: "auto",
                                },
                                className: "scroll",
                              },
                            }}
                            labelId="asset-compliance-label"
                            id="asset-compliance-select"
                            value={formData.assetCompliance}
                            onChange={(e) =>
                              handleInputChange("assetCompliance", e.target.value)
                            }
                            label="Asset Compliance *"
                            disabled={!isEditable}
                            renderValue={(selected) => (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                  maxWidth: "100%",
                                  overflow: "hidden",
                                  alignItems: "center",
                                  minHeight: "20px",
                                }}
                              >
                                {selected.map((value: any) => {
                                  const option = complianceOptions.find(
                                    (opt) => opt.value === value
                                  );
                                  return (
                                    <Chip
                                      key={value}
                                      label={option?.label || value}
                                      size="small"
                                      sx={{
                                        fontSize: "11px",
                                        height: "16px",
                                        maxWidth: "120px",
                                        "& .MuiChip-label": {
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        },
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {complianceOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Checkbox
                                  checked={
                                    formData.assetCompliance.indexOf(
                                      option.value
                                    ) > -1
                                  }
                                />
                                <ListItemText primary={option.label} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  {/* Resource Compliance - Only show for Resource Report and Resource Summary Report */}
                  {(selectedReportType?.name === "Resource Report" ||
                    selectedReportType?.name === "Resource Summary Report") && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <FormControl fullWidth sx={{ mt: 1 }}>
                          <InputLabel
                            id="resource-compliance-label"
                            sx={{ fontSize: "12px" }}
                          >
                            Resource Compliance *
                          </InputLabel>
                          <Select
                            multiple
                            sx={{
                              minHeight: "40px",
                              display: "flex",
                              alignItems: "center",
                              fontSize: "14px",
                              "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                                paddingTop: "8px",
                                paddingBottom: "8px",
                                paddingLeft: "14px",
                                paddingRight: "32px",
                                minHeight: "24px",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.23)",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.87)",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "primary.main",
                                borderWidth: "2px",
                              },
                            }}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: "300px",
                                  overflow: "auto",
                                },
                                className: "scroll",
                              },
                            }}
                            labelId="resource-compliance-label"
                            id="resource-compliance-select"
                            value={formData.resourceCompliance}
                            onChange={(e) =>
                              handleInputChange(
                                "resourceCompliance",
                                e.target.value
                              )
                            }
                            label="Resource Compliance *"
                            disabled={!isEditable}
                            renderValue={(selected) => (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                  width: "100%",
                                  alignItems: "center",
                                  minHeight: "20px",
                                  paddingTop: "2px",
                                  paddingBottom: "2px",
                                }}
                              >
                                {selected.map((value: any) => {
                                  const option = resourceComplianceOptions.find(
                                    (opt) => opt.value === value
                                  );
                                  return (
                                    <Chip
                                      key={value}
                                      label={option?.label || value}
                                      size="small"
                                      sx={{
                                        fontSize: "11px",
                                        height: "20px",
                                        maxWidth: "120px",
                                        "& .MuiChip-label": {
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          paddingLeft: "6px",
                                          paddingRight: "6px",
                                        },
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {resourceComplianceOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Checkbox
                                  checked={
                                    formData.resourceCompliance.indexOf(
                                      option.value
                                    ) > -1
                                  }
                                />
                                <ListItemText primary={option.label} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  {/* Report Format */}
                  {selectedReportType.name === "Organization Posture report" ? (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "nowrap",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: isDarkMode ? "#cccccc" : "#666666",
                            whiteSpace: "nowrap",
                            minWidth: "fit-content",
                            flexShrink: 0,
                          }}
                        >
                          Report format:
                        </Typography>
                        <RadioGroup
                          value={formData.reportFormat}
                          onChange={(e) =>
                            handleInputChange("reportFormat", e.target.value)
                          }
                          row
                          sx={{ flexWrap: "nowrap", gap: 0.5 }}
                        >
                          <FormControlLabel
                            value="Excel"
                            control={
                              <Radio
                                sx={{ color: "gray", padding: "6px" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Excel"
                            sx={{ mr: 0.5, whiteSpace: "nowrap" }}
                          />
                          <FormControlLabel
                            value="PDF"
                            control={
                              <Radio
                                sx={{ color: "gray", padding: "6px" }}
                                disabled={
                                  !isEditable ||
                                  selectedReportType?.name ===
                                  "Organization Posture report"
                                }
                              />
                            }
                            label="PDF"
                            sx={{ mr: 0, whiteSpace: "nowrap" }}
                          />
                        </RadioGroup>

                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: isDarkMode ? "#cccccc" : "#666666",
                            whiteSpace: "nowrap",
                            minWidth: "fit-content",
                            flexShrink: 0,
                            marginLeft: 2,
                          }}
                        >
                          On-prem Risks Accepted:
                        </Typography>
                        <RadioGroup
                          value={formData.orgOnPremRiskAccepted}
                          onChange={(e) =>
                            handleInputChange(
                              "orgOnPremRiskAccepted",
                              e.target.value
                            )
                          }
                          row
                          sx={{ flexWrap: "nowrap", gap: 0.5 }}
                        >
                          <FormControlLabel
                            value="Include"
                            control={
                              <Radio
                                sx={{ color: "gray", padding: "6px" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Include"
                            sx={{ mr: 0.5, whiteSpace: "nowrap" }}
                          />
                          <FormControlLabel
                            value="Exclude"
                            control={
                              <Radio
                                sx={{ color: "gray", padding: "6px" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Exclude"
                            sx={{ mr: 0, whiteSpace: "nowrap" }}
                          />
                        </RadioGroup>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "nowrap",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: isDarkMode ? "#cccccc" : "#666666",
                            whiteSpace: "nowrap",
                            minWidth: "fit-content",
                            flexShrink: 0,
                            marginLeft: 2,
                          }}
                        >
                          Security Posture:
                        </Typography>
                        <RadioGroup
                          value={formData.orgPostureScope}
                          onChange={(e) =>
                            handleInputChange("orgPostureScope", e.target.value)
                          }
                          row
                          sx={{ flexWrap: "nowrap", gap: 0.5 }}
                        >
                          {postureOptions.map((option) => (
                            <FormControlLabel
                              key={option.value}
                              value={option.value}
                              control={
                                <Radio
                                  sx={{ color: "gray", padding: "6px" }}
                                  disabled={!isEditable || option.disabled}
                                />
                              }
                              label={option.label}
                              sx={{ mr: 0.5, whiteSpace: "nowrap" }}
                            />
                          ))}
                        </RadioGroup>
                      </Box>
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "nowrap",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          fontWeight: "500",
                          color: isDarkMode ? "#cccccc" : "#666666",
                          whiteSpace: "nowrap",
                          minWidth: "fit-content",
                        }}
                      >
                        Report format:
                      </Typography>
                      <RadioGroup
                        value={formData.reportFormat}
                        onChange={(e) =>
                          handleInputChange("reportFormat", e.target.value)
                        }
                        row
                        sx={{ flexWrap: "nowrap" }}
                      >
                        <FormControlLabel
                          value="Excel"
                          control={
                            <Radio
                              sx={{ color: "gray" }}
                              disabled={!isEditable}
                            />
                          }
                          label="Excel"
                          sx={{ marginRight: 1, whiteSpace: "nowrap" }}
                        />
                        <FormControlLabel
                          value="PDF"
                          control={
                            <Radio
                              sx={{ color: "gray" }}
                              disabled={
                                !isEditable ||
                                selectedReportType?.name ===
                                "Organization CVE Report"
                              }
                            />
                          }
                          label="PDF"
                          sx={{ whiteSpace: "nowrap" }}
                        />
                      </RadioGroup>
                    </Box>
                  )}

                  {/* Other fields for Asset and Resource reports (not Organization reports) */}
                  {(selectedReportType?.name === "Asset Report" ||
                    selectedReportType?.name === "Resource Report") && (
                      <>
                        {/* Log Output */}
                        {!(
                          selectedReportType?.name === "Resource Report" ||
                          selectedReportType?.name === "Resource Summary Report"
                        ) && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "nowrap",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: "500",
                                  color: isDarkMode ? "#cccccc" : "#666666",
                                  whiteSpace: "nowrap",
                                  minWidth: "fit-content",
                                  flexShrink: 0,
                                }}
                              >
                                CVE Description:
                              </Typography>
                              <RadioGroup
                                value={formData.cveDescription}
                                onChange={(e) =>
                                  handleInputChange(
                                    "cveDescription",
                                    e.target.value
                                  )
                                }
                                row
                                sx={{ flexWrap: "nowrap", gap: 0.5 }}
                              >
                                <FormControlLabel
                                  value="Include"
                                  control={
                                    <Radio
                                      sx={{ color: "gray", padding: "6px" }}
                                      disabled={!isEditable}
                                    />
                                  }
                                  label="Include"
                                  sx={{
                                    mr: 0.5,
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: "13px",
                                    },
                                  }}
                                />
                                <FormControlLabel
                                  value="Exclude"
                                  control={
                                    <Radio
                                      sx={{ color: "gray", padding: "6px" }}
                                      disabled={!isEditable}
                                    />
                                  }
                                  label="Exclude"
                                  sx={{
                                    mr: 0,
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: "13px",
                                    },
                                  }}
                                />
                              </RadioGroup>
                            </Box>
                          )}

                        {/* Remediation */}
                        {/* Asset MITRE: - Only show for Asset reports */}
                        {!(
                          selectedReportType?.name === "Resource Report" ||
                          selectedReportType?.name === "Resource Summary Report"
                        ) && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "nowrap",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: "500",
                                  color: isDarkMode ? "#cccccc" : "#666666",
                                  whiteSpace: "nowrap",
                                  minWidth: "fit-content",
                                  flexShrink: 0,
                                }}
                              >
                                Asset MITRE:
                              </Typography>
                              <RadioGroup
                                value={formData.assetMitre}
                                onChange={(e) =>
                                  handleInputChange("assetMitre", e.target.value)
                                }
                                row
                                sx={{ flexWrap: "nowrap", gap: 0.5 }}
                              >
                                <FormControlLabel
                                  value="Include"
                                  control={
                                    <Radio
                                      sx={{ color: "gray", padding: "6px" }}
                                      disabled={!isEditable}
                                    />
                                  }
                                  label="Include"
                                  sx={{
                                    mr: 0.5,
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: "13px",
                                    },
                                  }}
                                />
                                <FormControlLabel
                                  value="Exclude"
                                  control={
                                    <Radio
                                      sx={{ color: "gray", padding: "6px" }}
                                      disabled={!isEditable}
                                    />
                                  }
                                  label="Exclude"
                                  sx={{
                                    mr: 0,
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: "13px",
                                    },
                                  }}
                                />
                              </RadioGroup>
                            </Box>
                          )}

                        {/* Risk Accepted */}
                        {/* MITRE Description - Only show for Asset reports */}
                        {!(
                          selectedReportType?.name === "Resource Report" ||
                          selectedReportType?.name === "Resource Summary Report"
                        ) && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "nowrap",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: "500",
                                  color: isDarkMode ? "#cccccc" : "#666666",
                                  whiteSpace: "nowrap",
                                  minWidth: "fit-content",
                                  flexShrink: 0,
                                }}
                              >
                                MITRE Description:
                              </Typography>
                              <RadioGroup
                                value={formData.mitreDescription}
                                onChange={(e) =>
                                  handleInputChange(
                                    "mitreDescription",
                                    e.target.value
                                  )
                                }
                                row
                                sx={{ flexWrap: "nowrap", gap: 0.5 }}
                              >
                                <FormControlLabel
                                  value="Include"
                                  control={
                                    <Radio
                                      sx={{ color: "gray", padding: "6px" }}
                                      disabled={!isEditable}
                                    />
                                  }
                                  label="Include"
                                  sx={{
                                    mr: 0.5,
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: "13px",
                                    },
                                  }}
                                />
                                <FormControlLabel
                                  value="Exclude"
                                  control={
                                    <Radio
                                      sx={{ color: "gray", padding: "6px" }}
                                      disabled={!isEditable}
                                    />
                                  }
                                  label="Exclude"
                                  sx={{
                                    mr: 0,
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: "13px",
                                    },
                                  }}
                                />
                              </RadioGroup>
                            </Box>
                          )}

                        {/* Resource MITRE - Only show for Resource reports */}
                        {(selectedReportType?.name === "Resource Report" ||
                          selectedReportType?.name ===
                          "Resource Summary Report") && (
                            <>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: isDarkMode ? "#cccccc" : "#666666",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Resource MITRE:
                                </Typography>
                                <RadioGroup
                                  value={formData.resourceMitre}
                                  onChange={(e) =>
                                    handleInputChange(
                                      "resourceMitre",
                                      e.target.value
                                    )
                                  }
                                  row
                                  sx={{ flexWrap: "nowrap" }}
                                >
                                  <FormControlLabel
                                    value="Include"
                                    control={
                                      <Radio
                                        sx={{ color: "gray" }}
                                        disabled={!isEditable}
                                      />
                                    }
                                    label="Include"
                                  />
                                  <FormControlLabel
                                    value="Exclude"
                                    control={
                                      <Radio
                                        sx={{ color: "gray" }}
                                        disabled={!isEditable}
                                      />
                                    }
                                    label="Exclude"
                                  />
                                </RadioGroup>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: isDarkMode ? "#cccccc" : "#666666",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  MITRE Description:
                                </Typography>
                                <RadioGroup
                                  value={formData.resourceMitreDescription}
                                  onChange={(e) =>
                                    handleInputChange(
                                      "resourceMitreDescription",
                                      e.target.value
                                    )
                                  }
                                  row
                                  sx={{ flexWrap: "nowrap" }}
                                >
                                  <FormControlLabel
                                    value="Include"
                                    control={
                                      <Radio
                                        sx={{ color: "gray" }}
                                        disabled={!isEditable}
                                      />
                                    }
                                    label="Include"
                                  />
                                  <FormControlLabel
                                    value="Exclude"
                                    control={
                                      <Radio
                                        sx={{ color: "gray" }}
                                        disabled={!isEditable}
                                      />
                                    }
                                    label="Exclude"
                                  />
                                </RadioGroup>
                              </Box>
                            </>
                          )}

                        {/* Recommendation */}
                        <Box
                          sx={{
                            gridColumn: "span 2",
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "14px",
                              fontWeight: "500",
                              color: isDarkMode ? "#cccccc" : "#666666",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Compliance Description:
                          </Typography>
                          <RadioGroup
                            value={formData.complianceDescription}
                            onChange={(e) =>
                              handleInputChange(
                                "complianceDescription",
                                e.target.value
                              )
                            }
                            row
                          >
                            <FormControlLabel
                              value="Include"
                              control={
                                <Radio
                                  sx={{ color: "gray" }}
                                  disabled={!isEditable}
                                />
                              }
                              label="Include"
                            />
                            <FormControlLabel
                              value="Exclude"
                              control={
                                <Radio
                                  sx={{ color: "gray" }}
                                  disabled={!isEditable}
                                />
                              }
                              label="Exclude"
                            />
                          </RadioGroup>
                        </Box>
                      </>
                    )}
                </Box>
              </Box>
            </Collapse>
          </Card>

          {/* Asset Posture - Show only for Asset and Resource reports (not Organization reports) */}
          {(selectedReportType?.name === "Asset Report" ||
            selectedReportType?.name === "Resource Report") && (
              <Card
                sx={{
                  mb: 2,
                  backgroundColor: isDarkMode ? "#2a2a2a" : "#f8f9fa",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 16px",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setIsAssetPostureExpanded(!isAssetPostureExpanded)
                  }
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    {selectedReportType?.name === "Resource Report" ||
                      selectedReportType?.name === "Resource Summary Report"
                      ? "Resource Posture"
                      : "Asset Posture"}
                  </Typography>
                  <IconButton size="small">
                    {isAssetPostureExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={isAssetPostureExpanded}>
                  <Box sx={{ padding: "0 16px 16px 16px" }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 1,
                      }}
                    >
                      <Box>
                        <FormControl fullWidth sx={{ mt: 1 }}>
                          <InputLabel
                            id="benchmarks-label"
                            sx={{ fontSize: "12px" }}
                          >
                            Benchmarks *
                          </InputLabel>
                          <Select
                            sx={{
                              height: "40px",
                              display: "flex",
                              alignItems: "center", // Center text vertically
                              fontSize: "14px",
                              "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center", // Center content vertically
                                paddingTop: "8px", // Optional: fine-tune vertical spacing
                                paddingBottom: "8px",
                              },
                            }}
                            labelId="benchmarks-label"
                            id="benchmarks-select"
                            value={formData.benchmarks}
                            onChange={(e) =>
                              handleInputChange("benchmarks", e.target.value)
                            }
                            label="Benchmarks *"
                            disabled={!isEditable}
                          >
                            <MenuItem value="0">CIS</MenuItem>
                            <MenuItem value="1">SPARK</MenuItem>
                            <MenuItem value="2">All</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "nowrap",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "13px",
                            fontWeight: "500",
                            color: isDarkMode ? "#cccccc" : "#666666",
                            whiteSpace: "nowrap",
                            minWidth: "fit-content",
                            flexShrink: 0,
                          }}
                        >
                          Log output:
                        </Typography>
                        <RadioGroup
                          value={formData.logOutput}
                          onChange={(e) =>
                            handleInputChange("logOutput", e.target.value)
                          }
                          row
                          sx={{ flexWrap: "nowrap", gap: 0.5 }}
                        >
                          <FormControlLabel
                            value="Include"
                            control={
                              <Radio
                                sx={{ color: "gray", padding: "6px" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Include"
                            sx={{
                              mr: 0.5,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "13px",
                              },
                            }}
                          />
                          <FormControlLabel
                            value="Exclude"
                            control={
                              <Radio
                                sx={{ color: "gray", padding: "6px" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Exclude"
                            sx={{
                              mr: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "13px",
                              },
                            }}
                          />
                        </RadioGroup>
                      </Box>

                      {/* Remediation */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "nowrap",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "13px",
                            fontWeight: "500",
                            color: isDarkMode ? "#cccccc" : "#666666",
                            whiteSpace: "nowrap",
                            minWidth: "fit-content",
                            flexShrink: 0,
                          }}
                        >
                          Remediation:
                        </Typography>
                        <RadioGroup
                          value={formData.remediation}
                          onChange={(e) =>
                            handleInputChange("remediation", e.target.value)
                          }
                          row
                          sx={{ flexWrap: "nowrap", gap: 0.5 }}
                        >
                          <FormControlLabel
                            value="Include"
                            control={
                              <Radio
                                sx={{ color: "gray", padding: "6px" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Include"
                            sx={{
                              mr: 0.5,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "13px",
                              },
                            }}
                          />
                          <FormControlLabel
                            value="Exclude"
                            control={
                              <Radio
                                sx={{ color: "gray", padding: "6px" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Exclude"
                            sx={{
                              mr: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "13px",
                              },
                            }}
                          />
                        </RadioGroup>
                      </Box>

                      {/* Risk Accepted */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "nowrap",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "13px",
                            fontWeight: "500",
                            color: isDarkMode ? "#cccccc" : "#666666",
                            whiteSpace: "nowrap",
                            minWidth: "fit-content",
                            flexShrink: 0,
                          }}
                        >
                          Risk Accepted:
                        </Typography>
                        <RadioGroup
                          value={formData.riskAccepted}
                          onChange={(e) =>
                            handleInputChange("riskAccepted", e.target.value)
                          }
                          row
                          sx={{ flexWrap: "nowrap", gap: 0.5 }}
                        >
                          <FormControlLabel
                            value="Include"
                            control={
                              <Radio
                                sx={{ color: "gray", padding: "6px" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Include"
                            sx={{
                              mr: 0.5,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "13px",
                              },
                            }}
                          />
                          <FormControlLabel
                            value="Exclude"
                            control={
                              <Radio
                                sx={{ color: "gray", padding: "6px" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Exclude"
                            sx={{
                              mr: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "13px",
                              },
                            }}
                          />
                        </RadioGroup>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "nowrap",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "13px",
                            fontWeight: "500",
                            color: isDarkMode ? "#cccccc" : "#666666",
                            whiteSpace: "nowrap",
                            minWidth: "fit-content",
                            flexShrink: 0,
                          }}
                        >
                          Description:
                        </Typography>
                        <RadioGroup
                          value={formData.descriptionInclude}
                          onChange={(e) =>
                            handleInputChange(
                              "descriptionInclude",
                              e.target.value
                            )
                          }
                          row
                          sx={{ flexWrap: "nowrap", gap: 0.5 }}
                        >
                          <FormControlLabel
                            value="Include"
                            control={
                              <Radio
                                sx={{ color: "gray", padding: "6px" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Include"
                            sx={{
                              mr: 0.5,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "13px",
                              },
                            }}
                          />
                          <FormControlLabel
                            value="Exclude"
                            control={
                              <Radio
                                sx={{ color: "gray", padding: "6px" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Exclude"
                            sx={{
                              mr: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "13px",
                              },
                            }}
                          />
                        </RadioGroup>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "nowrap",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "13px",
                            fontWeight: "500",
                            color: isDarkMode ? "#cccccc" : "#666666",
                            whiteSpace: "nowrap",
                            minWidth: "fit-content",
                            flexShrink: 0,
                          }}
                        >
                          Impact:
                        </Typography>
                        <RadioGroup
                          value={formData.impact}
                          onChange={(e) =>
                            handleInputChange("impact", e.target.value)
                          }
                          row
                          sx={{ flexWrap: "nowrap", gap: 0.5 }}
                        >
                          <FormControlLabel
                            value="Include"
                            control={
                              <Radio
                                sx={{ color: "gray", padding: "6px" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Include"
                            sx={{
                              mr: 0.5,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "13px",
                              },
                            }}
                          />
                          <FormControlLabel
                            value="Exclude"
                            control={
                              <Radio
                                sx={{ color: "gray", padding: "6px" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Exclude"
                            sx={{
                              mr: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "13px",
                              },
                            }}
                          />
                        </RadioGroup>
                      </Box>

                      {/* Recommendation */}
                      <Box
                        sx={{
                          gridColumn: "span 2",
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: isDarkMode ? "#cccccc" : "#666666",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Recommendation:
                        </Typography>
                        <RadioGroup
                          value={formData.recommendation}
                          onChange={(e) =>
                            handleInputChange("recommendation", e.target.value)
                          }
                          row
                        >
                          <FormControlLabel
                            value="1"
                            control={
                              <Radio
                                sx={{ color: "gray" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Failed"
                          />
                          <FormControlLabel
                            value="0"
                            control={
                              <Radio
                                sx={{ color: "gray" }}
                                disabled={!isEditable}
                              />
                            }
                            label="Passed"
                          />
                          <FormControlLabel
                            value="2"
                            control={
                              <Radio
                                sx={{ color: "gray" }}
                                disabled={!isEditable}
                              />
                            }
                            label="All"
                          />
                        </RadioGroup>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </Card>
            )}

          {/* Filters Settings - Show for Asset Report, Asset Summary Report, Organization Summary Report, Organization CVE Report, and Organization Compliance Report */}
          {(selectedReportType.name === "Asset Report" ||
            selectedReportType.name === "Asset Summary Report" ||
            selectedReportType.name === "Organization Summary Report" ||
            selectedReportType.name === "Organization CVE Report" ||
            selectedReportType.name === "Organization Compliance Report" ||
            selectedReportType.name === "Resource Report" ||
            selectedReportType.name === "Resource Summary Report" ||
            selectedReportType.name === "Organization Posture report") && (
              <Card
                sx={{
                  mb: 2,
                  backgroundColor: isDarkMode ? "#2a2a2a" : "#f8f9fa",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 16px",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setIsFiltersSettingsExpanded(!isFiltersSettingsExpanded)
                  }
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    Filters Settings
                  </Typography>
                  <IconButton size="small">
                    {isFiltersSettingsExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={isFiltersSettingsExpanded}>
                  <Box sx={{ padding: "0 16px 16px 16px" }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          selectedReportType.name === "Resource Report" ||
                            selectedReportType.name === "Resource Summary Report"
                            ? "1fr 1fr"
                            : "repeat(2, 1fr)",
                        gap: 3,
                      }}
                    >
                      {/* Assets */}
                      {(selectedReportType.name === "Asset Report" ||
                        selectedReportType.name === "Asset Summary Report" ||
                        selectedReportType.name ===
                        "Organization Summary Report" ||
                        selectedReportType.name === "Organization CVE Report" ||
                        selectedReportType.name ===
                        "Organization Compliance Report" ||
                        selectedReportType.name ===
                        "Organization Posture report") && (
                          <Box>
                            {selectedReportType.name === "Asset Report" && (
                              <FormControl fullWidth sx={{ mt: 1 }}>
                                <InputLabel
                                  id="assets-label"
                                  sx={{ fontSize: "12px" }}
                                >
                                  Assets *
                                </InputLabel>
                                <Select
                                  sx={{
                                    height: "40px",
                                    display: "flex",
                                    alignItems: "center", // Center text vertically
                                    fontSize: "14px",
                                    "& .MuiSelect-select": {
                                      display: "flex",
                                      alignItems: "center", // Center content vertically
                                      paddingTop: "8px", // Optional: fine-tune vertical spacing
                                      paddingBottom: "8px",
                                    },
                                  }}
                                  labelId="assets-label"
                                  id="assets-select"
                                  value={formData.assets}
                                  onChange={(e) =>
                                    handleInputChange("assets", e.target.value)
                                  }
                                  label="Assets *"
                                  disabled={!isEditable}
                                >
                                  {/* For Asset Report: Show device names from asset-list */}
                                  {selectedReportType.name === "Asset Report" &&
                                    assetData.map((asset: any) => (
                                      <MenuItem key={asset.id} value={asset.id}>
                                        {asset.device_name}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                            )}
                            {(selectedReportType.name === "Asset Summary Report" ||
                              selectedReportType.name ===
                              "Organization Summary Report" ||
                              selectedReportType.name ===
                              "Organization CVE Report" ||
                              selectedReportType.name ===
                              "Organization Compliance Report" ||
                              selectedReportType.name ===
                              "Organization Posture report") && (
                                <FormControl fullWidth sx={{ mt: 1 }}>
                                  <InputLabel
                                    id="assets-label"
                                    sx={{ fontSize: "12px" }}
                                  >
                                    Assets *
                                  </InputLabel>
                                  <Select
                                    sx={{
                                      height: "40px",
                                      display: "flex",
                                      alignItems: "center", // Center text vertically
                                      fontSize: "14px",
                                      "& .MuiSelect-select": {
                                        display: "flex",
                                        alignItems: "center", // Center content vertically
                                        paddingTop: "8px", // Optional: fine-tune vertical spacing
                                        paddingBottom: "8px",
                                      },
                                    }}
                                    labelId="assets-label"
                                    id="assets-select"
                                    value={formData.assets}
                                    onChange={(e) =>
                                      handleInputChange("assets", e.target.value)
                                    }
                                    label="Assets *"
                                    disabled={!isEditable}
                                  >
                                    <MenuItem value="0">All</MenuItem>
                                    <MenuItem value="1">Global Filter</MenuItem>
                                    <MenuItem value="2">Custom</MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              {/* Asset count display */}
                              {formData.assets === "2" &&
                                selectedAssetsCount > 0 && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "12px",
                                      color: isDarkMode ? "#cccccc" : "#666666",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {`${selectedAssetsCount} assets are selected`}
                                  </Typography>
                                )}

                              {/* Select Assets button */}
                              {formData.assets === "2" && (
                                <Button
                                  onClick={handleSelectAssets}
                                  startIcon={
                                    <Box
                                      sx={{
                                        width: "20px",
                                        height: "20px",
                                        borderRadius: "50%",
                                        backgroundColor: "blue",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Add
                                        sx={{ color: "white", fontSize: "12px" }}
                                      />
                                    </Box>
                                  }
                                  variant="text"
                                  sx={{
                                    color: "blue",
                                    textTransform: "none",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    mt: "12px",
                                    "&.Mui-disabled": {
                                      backgroundColor: "transparent",
                                      color: "blue",
                                      opacity: 0.5,
                                    },
                                  }}
                                  disabled={!isEditable}
                                >
                                  Select Assets
                                </Button>
                              )}
                            </Box>
                          </Box>
                        )}

                      {/* Resource Selection for Resource Report */}
                      {selectedReportType.name === "Resource Report" && (
                        <Box>
                          <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel
                              id="resource-label"
                              sx={{ fontSize: "12px" }}
                            >
                              Resource *
                            </InputLabel>
                            <Select
                              sx={{
                                height: "40px",
                                display: "flex",
                                alignItems: "center", // Center text vertically
                                fontSize: "14px",
                                "& .MuiSelect-select": {
                                  display: "flex",
                                  alignItems: "center", // Center content vertically
                                  paddingTop: "8px", // Optional: fine-tune vertical spacing
                                  paddingBottom: "8px",
                                },
                                "& .MuiMenu-paper": {
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                },
                              }}
                              slotProps={{
                                input: {
                                  sx: {
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                  },
                                },
                                paper: {
                                  className: "scroll",
                                  sx: {
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  },
                                },
                              }}
                              MenuProps={{
                                PaperProps: {
                                  className: "scroll",
                                  sx: {
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  },
                                },
                              }}
                              labelId="resource-label"
                              id="resource-select"
                              value={formData.resource}
                              onChange={(e) =>
                                handleInputChange("resource", e.target.value)
                              }
                              label="Resource *"
                              disabled={!isEditable}
                            >
                              {/* Show cloud resource names from API */}
                              {cloudResourceData.map((resource: any) => (
                                <MenuItem key={resource.id} value={resource.id}>
                                  {resource.resource_name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      )}

                      {/* Resource Selection for Resource Summary Report */}
                      {selectedReportType.name === "Resource Summary Report" && (
                        <Box>
                          <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel
                              id="resource-summary-label"
                              sx={{ fontSize: "12px" }}
                            >
                              Resource *
                            </InputLabel>
                            <Select
                              sx={{
                                height: "40px",
                                display: "flex",
                                alignItems: "center", // Center text vertically
                                fontSize: "14px",
                                "& .MuiSelect-select": {
                                  display: "flex",
                                  alignItems: "center", // Center content vertically
                                  paddingTop: "8px", // Optional: fine-tune vertical spacing
                                  paddingBottom: "8px",
                                },
                              }}
                              labelId="resource-summary-label"
                              id="resource-summary-select"
                              value={formData.resource}
                              onChange={(e) =>
                                handleInputChange("resource", e.target.value)
                              }
                              label="Resource *"
                              disabled={!isEditable}
                            >
                              <MenuItem value="0">All</MenuItem>
                              <MenuItem value="2">Custom</MenuItem>
                            </Select>
                          </FormControl>

                          {/* Resource count and select button */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            {/* Resource count display */}
                            {formData.resource === "2" &&
                              selectedResourcesCount > 0 && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "12px",
                                    color: isDarkMode ? "#cccccc" : "#666666",
                                    fontStyle: "italic",
                                  }}
                                >
                                  {`${selectedResourcesCount} resources are selected`}
                                </Typography>
                              )}

                            {/* Select Resources button */}
                            {formData.resource === "2" && (
                              <Button
                                onClick={handleSelectResources}
                                startIcon={
                                  <Box
                                    sx={{
                                      width: "20px",
                                      height: "20px",
                                      borderRadius: "50%",
                                      backgroundColor: "blue",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Add
                                      sx={{ color: "white", fontSize: "12px" }}
                                    />
                                  </Box>
                                }
                                variant="text"
                                sx={{
                                  color: "blue",
                                  textTransform: "none",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  mt: "12px",
                                }}
                                disabled={!isEditable}
                              >
                                Select Resources
                              </Button>
                            )}
                          </Box>
                        </Box>
                      )}

                      {/* User */}
                      {/* <Box>
                                    <FormControl fullWidth sx={{ mt: 1 }}>
                                        <InputLabel id="user-label" sx={{ fontSize: '12px' }}>User *</InputLabel>
                                        <Select
                                            sx={{
                                                height: '40px',
                                                display: 'flex',
                                                alignItems: 'center', // Center text vertically
                                                fontSize: '14px',
                                                '& .MuiSelect-select': {
                                                    display: 'flex',
                                                    alignItems: 'center', // Center content vertically
                                                    paddingTop: '8px',     // Optional: fine-tune vertical spacing
                                                    paddingBottom: '8px',
                                                },
                                            }}
                                            labelId="user-label"
                                            id="user-select"
                                            value={formData.user}
                                            onChange={(e) => handleInputChange("user", e.target.value)}
                                            label="User *"
                                            disabled={!isEditable}
                                        >
                                            <MenuItem value="1">All Users</MenuItem>
                                            <MenuItem value="self">self</MenuItem>
                                            <MenuItem value="2">Customise</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        {formData.user === "2" && selectedUsers.length > 0 && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: '12px',
                                                    color: isDarkMode ? '#cccccc' : '#666666',
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                {selectedUsers.length} users are selected
                                            </Typography>
                                        )}

                                        {formData.user === "2" && (
                                            <Button
                                                onClick={handleSelectUsers}
                                                startIcon={
                                                    <Box
                                                        sx={{
                                                            width: '20px',
                                                            height: '20px',
                                                            borderRadius: '50%',
                                                            backgroundColor: 'blue',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Add sx={{ color: 'white', fontSize: '12px' }} />
                                                    </Box>
                                                }
                                                variant="text"
                                                sx={{
                                                    color: 'blue',
                                                    textTransform: 'none',
                                                    fontSize: '14px',
                                                    fontWeight: '500',
                                                    mt: '12px',
                                                }}
                                                disabled={!isEditable}
                                            >
                                                Select Users
                                            </Button>
                                        )}
                                    </Box>
                                </Box> */}
                    </Box>
                  </Box>
                </Collapse>
              </Card>
            )}
        </div>

        {/* Resource Report Customise Drawer */}
        <ResourceReportCustomise
          open={resourcesLoader}
          onClose={() => setResourcesLoader(false)}
          skipApiCall={true}
          onFilterDataSaved={handleFilterDataSaved}
          preSelectedResourceIds={preSelectedResourceIds}
        />

        {/* Assets Drawer */}
        <Drawer
          variant="temporary"
          anchor="right"
          open={assetsLoader}
          onClose={() => setAssetsLoader(false)}
          sx={{
            "& .MuiDrawer-paper": {
              width: 500,
              height: "100vh",
              boxSizing: "border-box",
              position: "fixed",
              top: 0,
              zIndex: 1201,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              overflow: "hidden",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "-4px 0 15px rgba(0, 0, 0, 0.3)"
                  : "-4px 0 15px rgba(0, 0, 0, 0.1)",
            },
            "& .MuiBackdrop-root": {
              backdropFilter: "none",
              backgroundColor: "transparent",
            },
          }}
        >
          <GlobalFilter
            setIsLoader={setAssetsLoader}
            toggleGlobalFilter={() => setAssetsLoader(false)}
            skipApiCall={true}
            onFilterDataSaved={handleFilterDataSaved}
            preSelectedAssetIds={preSelectedAssetIds}
            clearFilters={!isEditMode}
            hideCloud={selectedReportType?.name === "Asset Summary Report"}
          />
        </Drawer>

        {/* Users Drawer */}
        <Drawer
          variant="temporary"
          anchor="right"
          open={usersLoader}
          onClose={() => setUsersLoader(false)}
          sx={{
            "& .MuiDrawer-paper": {
              width: 550,
              height: "100vh",
              boxSizing: "border-box",
              position: "fixed",
              top: 0,
              zIndex: 1201,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              overflow: "hidden",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "-4px 0 15px rgba(0, 0, 0, 0.3)"
                  : "-4px 0 15px rgba(0, 0, 0, 0.1)",
            },
            "& .MuiBackdrop-root": {
              backdropFilter: "none",
              backgroundColor: "transparent",
            },
          }}
        >
          <UserSelection
            onClose={() => setUsersLoader(false)}
            onDone={handleUsersSelected}
            previouslySelectedUsers={selectedUsers}
          />
        </Drawer>

        {/* Schedule Drawer */}
        <Drawer
          variant="temporary"
          anchor="right"
          open={scheduleDrawerOpen}
          onClose={() => setScheduleDrawerOpen(false)}
          sx={{
            "& .MuiDrawer-paper": {
              width: 480,
              height: "100vh",
              boxSizing: "border-box",
              position: "fixed",
              top: 0,
              zIndex: 1201,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "-4px 0 15px rgba(0, 0, 0, 0.3)"
                  : "-4px 0 15px rgba(0, 0, 0, 0.1)",
            },
            "& .MuiBackdrop-root": {
              backdropFilter: "none",
              backgroundColor: "transparent",
            },
          }}
        >
          <div
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #E2E2F2",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontSize: "16px", fontWeight: 600 }}
              >
                Schedule Report
              </Typography>
              <IconButton
                onClick={() => setScheduleDrawerOpen(false)}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </div>

            <div
              className="scroll"
              style={{ padding: 20, flex: 1, overflow: "auto" }}
            >
              {scheduleLoading ? (
                <Box sx={{ height: "100%" }} ref={leftGridRef}>
                  <SkeletonLoader
                    count={Math.floor(window.innerHeight / 40)}
                    styles={{ height: 30 }}
                  />
                </Box>
              ) : (
                <ScheduleForm
                  isScheduled={true}
                  selectedFrequency={selectedFrequency}
                  handleFrequencyChange={handleFrequencyChange}
                  scheduleOptions={scheduleOptions}
                  isOtherFrequency={isOtherFrequency}
                  selectedDate={selectedDate}
                  handleDateChange={handleDateChange}
                  isWeekly={isWeekly}
                  isHourly={isHourly}
                  selectedDay={selectedDay}
                  handleDayChange={handleDayChange}
                  selectedHour={selectedHour}
                  selectedMinute={selectedMinute}
                  selectedAmPm={selectedAmPm}
                  handleHourChange={handleHourChange}
                  handleMinuteChange={handleMinuteChange}
                  handleAmPmChange={handleAmPmChange}
                  hourlyInterval={hourlyInterval}
                  hourlyMinute={hourlyMinute}
                  handleHourlyIntervalChange={handleHourlyIntervalChange}
                  handleHourlyMinuteChange={handleHourlyMinuteChange}
                />
              )}
            </div>
            <div
              style={{
                padding: 16,
                borderTop: "1px solid #E2E2F2",
                display: "flex",
                gap: 8,
              }}
            >
              <Button
                fullWidth
                variant="outlined"
                disabled={
                  isLoading ||
                  processingButton === "schedule" ||
                  !isScheduleFormValid()
                }
                onClick={(e) => handleSubmit(e, "schedule")}
              >
                {isLoading && processingButton === "schedule"
                  ? "Processing..."
                  : "Schedule"}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                disabled={
                  isLoading ||
                  processingButton === "download" ||
                  !isScheduleFormValid()
                }
                onClick={(e) => handleSubmit(e, "download")}
              >
                {isLoading && processingButton === "download"
                  ? "Processing..."
                  : "Schedule & Download"}
              </Button>
            </div>
          </div>
        </Drawer>
      </>
    );
  }
);

export default CreateNewReport;
